import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
const REPORT_SECTIONS_KEY = 'preview_report_sections';
import html2pdf from 'html2pdf.js';
import { Router } from '@angular/router';
import { SharedStateService } from 'src/app/services/category.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { LocaleService } from 'src/app/services/locale';
import { LogoComponent } from 'src/app/components/icons/logo';
import { MinimizeComponent } from 'src/app/components/icons/minimize';
import { ButtonComponent } from 'src/app/components/shared/button';
import { ReportPreviewComponent } from './report-preview/report-preview.component';
import { APIService } from 'src/app/services/api';
import { uploadData } from '@aws-amplify/storage';
import { Chart } from 'chart.js/auto';
import { PdfGeneratorService } from 'src/app/services/pdf-generator.service';
import type { TooltipItem } from 'chart.js';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';

type CategoryKey =
  | 'category1'
  | 'category2'
  | 'category3'
  | 'category4'
  | 'category5'
  | 'category6';

interface ReportSection {
  id: number;
  title: string;
  content: string;
  isEditing: boolean;
}

@Component({
  selector: 'app-generate-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CKEditorModule,
    NgxSpinnerModule,
    LogoComponent,
    MinimizeComponent,
    ButtonComponent,
    ReportPreviewComponent,
  ],
  templateUrl: './generate-report.component.html',
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      .highlight {
        font-weight: bold;
      }

      /* Page break utilities for PDF generation */
      .page-break-before {
        page-break-before: always !important;
        break-before: page !important;
      }

      .page-break-after {
        page-break-after: always !important;
        break-after: page !important;
      }

      .avoid-break {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }

      /* Allow sections to break across pages but keep headers with content */
      .report-section {
        page-break-inside: auto !important;
        break-inside: auto !important;
      }

      /* List styles for report sections */
      .report-section ul {
        list-style-type: none !important;
        padding-left: 24px !important;
        margin: 10px 0 !important;
      }

      .report-section ol {
        list-style-type: none !important;
        padding-left: 24px !important;
        margin: 10px 0 !important;
      }

      .report-section li {
        display: list-item !important;
        margin: 5px 0 !important;
      }

      .ck-content ul {
        list-style-type: none !important;
        padding-left: 24px !important;
      }

      .ck-content ol {
        list-style-type: none !important;
        padding-left: 24px !important;
      }

      .ck-content li {
        display: list-item !important;
      }

      .pdf-page {
        width: 100%;
        background-color: white !important;
        background: #ffffff !important;
        color: #111827;
        font-size: 14px;
        line-height: 1.5;
      }

      /* Table styles for PDF */
      .pdf-page table,
      .preview-report table,
      .preview-modal-content table {
        width: 100% !important;
        max-width: 100% !important;
        table-layout: fixed !important;
        border-collapse: collapse !important;
        font-size: 12px !important;
        border: 1px solid #e5e7eb !important;
      }

      .pdf-page table th,
      .pdf-page table td,
      .preview-report table th,
      .preview-report table td,
      .preview-modal-content table th,
      .preview-modal-content table td {
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        word-break: break-word !important;
        white-space: normal !important;
        padding: 8px 4px !important;
        font-size: 12px !important;
        vertical-align: middle !important;
        border: 1px solid #e5e7eb !important;
      }

      /* Headings - only avoid breaking inside, allow break after */
      .pdf-page h1,
      .pdf-page h2,
      .pdf-page h3,
      .pdf-page h4,
      .pdf-page h5,
      .pdf-page h6 {
        break-inside: avoid;
        page-break-inside: avoid;
        margin-top: 0.5rem;
        margin-bottom: 0.5rem;
      }

      /* Paragraphs - allow breaking across pages */
      .pdf-page p {
        margin-top: 0.5rem;
        margin-bottom: 0.5rem;
        line-height: 1.6;
      }

      /* List items */
      .pdf-page li {
        margin-bottom: 0.25rem;
      }

      /* Lists */
      .pdf-page ul,
      .pdf-page ol {
        margin-bottom: 0.5rem;
      }

      /* Images - avoid splitting */
      .pdf-page img,
      .pdf-page svg,
      .pdf-page canvas {
        break-inside: avoid;
        page-break-inside: avoid;
        max-width: 100%;
      }

      /* Table rows - allow html2pdf to slice; splitTablesForPageBreaks handles breaks */
      .pdf-page tr {
        break-inside: auto !important;
        page-break-inside: auto !important;
      }

      /* Force new page for each section - allow content to flow within */
      .pdf-page .report-section {
        break-before: page;
        page-break-before: always;
        margin-bottom: 0;
        padding: 0 !important;
        box-shadow: none !important;
        border: none !important;
      }

      /* Exception: First section shouldn't force a break */
      .pdf-page .report-section:first-of-type {
        break-before: auto;
        page-break-before: auto;
      }

      /* Reduce gap before table inside flex containers */
      .pdf-page .flex.flex-col > table,
      .pdf-page .mb-4 > table {
        margin-top: 0.25rem !important;
      }

      .ui-only {
        pointer-events: auto;
      }

      @media print {
        .ui-only {
          display: none !important;
        }
      }

      /* Preview Modal Styles */
      .preview-modal-content {
        font-size: 14px;
        line-height: 1.6;
        color: #111827;
      }

      .preview-modal-content h1,
      .preview-modal-content h2,
      .preview-modal-content h3 {
        color: #043b22;
        margin-top: 0.5rem;
        margin-bottom: 0.5rem;
      }

      .preview-modal-content img {
        max-width: 100%;
        height: auto;
      }
    `,
  ],
})
export class GenerateReportComponent implements OnInit {
  private spinner = inject(NgxSpinnerService);
  private localeService = inject(LocaleService);
  private cdr = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);
  private toastr = inject(ToastrService);
  locale = this.localeService.locale();
  calculationId: any = null;
  currentLanguage = this.localeService.getLanguage();
  companyDetailsRecord: any = null;
  public Editor: any = ClassicEditor;
  companyDetails: any = {};

  get formattedCompanyAddress(): string {
    return parseCompanyAddress(this.companyDetails?.address).formattedAddress;
  }
  public editorConfig = {
    toolbar: [
      'heading',
      '|',
      'bold',
      'italic',
      'link',
      '|',
      'bulletedList',
      'numberedList',
      '|',
      'imageUpload',
      'imageStyle:inline',
      'imageStyle:block',
      'imageStyle:side',
      '|',
      'undo',
      'redo',
    ],
    image: {
      toolbar: [
        'imageStyle:inline',
        'imageStyle:block',
        'imageStyle:side',
        '|',
        'imageTextAlternative',
        '|',
        'resizeImage:50',
        'resizeImage:75',
        'resizeImage:original',
      ],
      resizeOptions: [
        { name: 'resizeImage:original', value: null, label: 'Original' },
        { name: 'resizeImage:50', value: '50', label: '50%' },
        { name: 'resizeImage:75', value: '75', label: '75%' },
      ],
      resizeUnit: '%' as any,
    },
  };
  sections: ReportSection[] = [];
  currentLogo: string = environment.imagesBaseUrl + '/foodprint-logo.svg';
  showPreviewModal: boolean = false;
  previewContent: string = '';
  private placeholderRegex = /\[[^\]]+\]/g;
  validationErrors: string[] = [];
  showValidationModal = false;
  categories: any[] = [];

  onReady(editor: any) {
    editor.plugins.get('FileRepository').createUploadAdapter = (
      loader: any,
    ) => {
      return {
        upload: () =>
          loader.file.then(async (file: File) => {
            try {
              // Generate unique filename
              const fileName = `report-images/${Date.now()}_${file.name}`;

              // Upload to S3
              await uploadData({
                path: fileName,
                data: file,
              }).result;

              // Construct permanent public URL (not presigned)
              const imageUrl = `https://${environment.s3.bucket}.s3.${environment.s3.region}.amazonaws.com/${fileName}`;

              // Return URL to CKEditor
              return { default: imageUrl };
            } catch (error) {
              console.error('Image upload failed:', error);
              throw error;
            }
          }),
        abort: () => {},
      };
    };
  }

  constructor(
    private router: Router,
    private currentcat: SharedStateService,
    private api: APIService,
    private pdfService: PdfGeneratorService,
  ) {
    effect(() => {
      this.locale = this.localeService.locale();
      this.currentLanguage = this.localeService.getLanguage();
    });
  }

  async ngOnInit() {
    this.spinner.show();
    // this.companyDetails = JSON.parse(localStorage.getItem("companyDetails") ?? "{}");
    this.calculationId = localStorage.getItem('calculationId') ?? '';
    const calculationData = await this.api.GetCompanyDetails(
      this.calculationId,
    );
    // console.log("calculationData ", calculationData);
    this.companyDetails = await this.api.GetCompany(calculationData.companyId);
    if (!this.companyDetails?.id) {
      console.error('companyId missing in companyDetails');
      return;
    }
    const categories = JSON.parse(localStorage.getItem('categories') || '[]');
    (window as any).updateEmissionSource = this.updateEmissionSource.bind(this);
    (window as any).saveEmissionSources = this.saveEmissionSources.bind(this);
    (window as any).updateDataSource = this.updateDataSource.bind(this);

    // Check LocalStorage keys for logo preferences
    const lsSetLogo = localStorage.getItem('setLogo');
    const lsCompanyLogo = localStorage.getItem('companyLogo');
    const lsProfilePhoto = localStorage.getItem('profilePhoto');

    // Determine priority: setLogo > companyLogo (LS) > profilePhoto (LS) > companyDetails > default
    // Handle potential string "null" or "undefined" from localStorage
    const valid = (val: string | null) =>
      val && val !== 'null' && val !== 'undefined' && val !== '';

    // Helper to ensure full URL path
    const ensureFullPath = (path: string): string => {
      if (!path) return environment.imagesBaseUrl + '/foodprint-logo.svg';
      if (
        path.startsWith('http://') ||
        path.startsWith('https://') ||
        path.startsWith('data:')
      ) {
        return path;
      }
      // If relative path, prepend base URL
      return environment.baseUrl + (path.startsWith('/') ? path : '/' + path);
    };
    console.log(this.companyDetails);
    const logoPath = valid(lsSetLogo)
      ? lsSetLogo!
      : valid(lsCompanyLogo)
        ? lsCompanyLogo!
        : valid(lsProfilePhoto)
          ? lsProfilePhoto!
          : valid(this.companyDetails?.companyLogo)
            ? this.companyDetails.companyLogo
            : valid(this.companyDetails?.profilePhoto)
              ? this.companyDetails.profilePhoto
              : environment.imagesBaseUrl + '/foodprint-logo.svg';

    this.currentLogo = ensureFullPath(logoPath);

    // Load report from DB (or load defaults and save if blank)
    await this.loadReportFromDB();

    this.loadSignificanceMatrix();
    this.injectSection6Content();
    this.injectSection7Content();
    this.injectSection8Content();
    this.injectSection10Content();
  }

  private injectSection7Content() {
    const section7 = this.sections.find((s) => s.id === 7);
    if (!section7) return;

    const generated = this.buildSection7Table();

    // Assign content FIRST so canvas elements are in the DOM
    if (section7.content !== generated) {
      section7.content = generated;
      section7.isEditing = false;

      // 🔥 THIS is what makes Preview work
      this.saveSectionsToLocal();
    }

    // Render charts AFTER content is assigned and DOM is updated
    setTimeout(() => {
      this.renderCategoryCharts();
      this.renderTotalEmissionsPieChart();
    }, 1000);
  }

  getInventoryOverallUncertaintyPct(significantCategories: string[]): number {
    const categories = JSON.parse(localStorage.getItem('categories') || '[]');

    if (!categories.length || !significantCategories?.length) {
      return 0;
    }

    let totalEmissionsKg = 0;
    let weightedVarianceSum = 0;
    let missingUncertaintyCount = 0;

    for (const catId of significantCategories) {
      const cat = categories.find((c: any) => c.id === catId);
      if (!cat?.questions?.length) continue;

      for (const q of cat.questions) {
        const emissionsArray = Array.isArray(q.emissions) ? q.emissions : [];
        if (!emissionsArray.length) continue;

        let questionCo2eKg = 0;

        for (const e of emissionsArray) {
          const normalized =
            typeof e === 'number'
              ? { co2e: e, constituent_gases: { co2e_total: e } }
              : e?.response || e?.estimate || e || {};
          const co2eKg = Number(
            normalized?.constituent_gases?.co2e_total ?? normalized?.co2e ?? 0,
          );
          questionCo2eKg += co2eKg;
        }

        if (questionCo2eKg <= 0) continue;

        // Get uncertainty for this question
        const matched = cat.uncertaintyMatrix?.find(
          (u: any) => u.questionId === q.id,
        );
        const uncPct = Number(matched?.totalUncertaintyPercent);

        // Do NOT silently treat missing uncertainty as 0
        if (!isFinite(uncPct) || uncPct <= 0) {
          missingUncertaintyCount++;
          continue;
        }

        totalEmissionsKg += questionCo2eKg;

        const u = uncPct / 100;
        weightedVarianceSum += Math.pow(questionCo2eKg * u, 2);
      }
    }

    // Force "N/A" in report if any emitting question lacks uncertainty
    if (missingUncertaintyCount > 0) {
      return Number.NaN;
    }

    if (totalEmissionsKg <= 0) {
      return 0;
    }

    const overall = Math.sqrt(weightedVarianceSum) / totalEmissionsKg;
    return Number((overall * 100).toFixed(2));
  }

  updateEmissionSource(categoryId: string, value: string): void {
    const categories = JSON.parse(localStorage.getItem('categories') || '[]');
    this.categories = categories.map((cat: any) => {
      if (cat.id === categoryId) {
        return { ...cat, emissionSource: value };
      }
      return cat;
    });
  }

  updateDataSource(categoryId: string, value: string): void {
    const categories = JSON.parse(localStorage.getItem('categories') || '[]');
    this.categories = this.categories.map((cat: any) => {
      if (cat.id === categoryId) {
        return { ...cat, dataSource: value };
      }
      return cat;
    });
  }

  saveEmissionSources = async (): Promise<void> => {
    await this.api.UpdateCompanyDetails({
      id: localStorage.getItem('calculationId') ?? '',
      answers: JSON.stringify(this.categories),
    });
    localStorage.setItem('categories', JSON.stringify(this.categories));
  };

  private buildSection7Table(): string {
    const isEn = this.currentLanguage === 'english';
    let categories = JSON.parse(localStorage.getItem('categories') || '[]');
    const categoryTotals: Record<string, any> = {};
    categories.forEach((cat: any) => {
      categoryTotals[cat.id] = cat.calculation_category_totals;
    });
    if (!this.significanceRows.length) {
      return `<p class="text-sm text-gray-500">${isEn ? 'No significance data available.' : 'Nessun dato di significatività disponibile.'}</p>`;
    }

    const buildCategoryChartCanvas = (categoryId: string) => {
      return `
        <div style="display:flex; justify-content:center; width:100%; margin-top:20px; margin-bottom:30px;">
          <div style="width:100%; max-width:900px; height:360px;">
            <canvas id="chart_${categoryId}"></canvas>
          </div>
        </div>
      `;
    };
    const buildTotalEmissionsPieCanvas = () => {
      return `
        <div style="position:relative; width:100%; max-width:500px; margin:25px auto; display:flex; justify-content:center; align-items:center;">
          <div style="max-width:100%; max-height:400px;">
            <canvas id="emissionsPieChart"></canvas>
          </div>
        </div>
      `;
    };

    // CATEGORY TABLE BUILDER
    const buildCategoryEmissionsTable = (categoryId: string) => {
      const category = categories.find((c: any) => c.id === categoryId);

      // Check LS exclusion first
      if (!includedCategories.includes(categoryId)) {
        return `${buildLSExclusionNote(categoryId)}`;
      }

      // Category is included but has no questions/data
      if (!category || !category.questions?.length) {
        return `<p class="text-sm text-gray-500 italic">${
          isEn
            ? 'This category has been excluded from the analysis because no activity data is available.'
            : "Questa categoria è stata esclusa dall'analisi in quanto non sono disponibili dati di attività."
        }</p>`;
      }

      let totalCO2 = 0;
      let totalCH4 = 0;
      let totalN2O = 0;
      let totalCO2e = 0;
      let weightedVarianceSum = 0;
      let totalEmissionsForUncertainty = 0;
      let missingUncertaintyCount = 0;
      let hasEmissions = false;
      const rows = category.questions
        .map((q: any) => {
          if (q.hidden) return '';
          const emissionsArray = Array.isArray(q.emissions) ? q.emissions : [];
          if (!emissionsArray.length) return '';

          return emissionsArray
            .map((emissionObj: any, index: number) => {
              // SAFE NORMALIZATION
              let normalizedEmission: any = {};

              if (typeof emissionObj === 'number') {
                normalizedEmission = {
                  co2e: emissionObj,
                  constituent_gases: { co2e_total: emissionObj },
                  activity_data: {},
                };
              } else {
                normalizedEmission =
                  emissionObj?.response ||
                  emissionObj?.estimate ||
                  emissionObj ||
                  {};
              }

              const gases = normalizedEmission.constituent_gases || {};
              const activityMeta = normalizedEmission.activity_data || {};

              let activity = q.title || '—';
              let activityData = 0;

              if (q.answers?.value !== undefined) {
                activityData = Number(q.answers.value || 0);
              }

              if (!activityData && typeof q.answers === 'object') {
                const keys = [
                  'ann',
                  'annual_emissions',
                  'money_amount',
                  'quantity',
                  'liters',
                  'kilometers',
                  'units_sold',
                  'fuel_lifetime_quantity',
                  'floorArea',
                  'annualDistance',
                  'activity_value',
                ];
                for (const key of keys) {
                  if (q.answers?.[key] !== undefined) {
                    activityData = Number(q.answers[key] || 0);
                    break;
                  }
                }
              }

              if (!activityData && Array.isArray(q.answers)) {
                const first = q.answers[index] || q.answers[0];
                const keys = [
                  'value',
                  'quantity',
                  'liters',
                  'kilometers',
                  'money_amount',
                  'activity_value',
                ];
                for (const key of keys) {
                  if (first?.[key] !== undefined) {
                    activityData = Number(first[key] || 0);
                    break;
                  }
                }
              }

              if (!activityData && q.answers?.productGroups?.length) {
                const pg = q.answers.productGroups[0];
                activityData = Number(pg?.units_sold || pg?.N_sold || 0);
              }

              if (!activityData && activityMeta.activity_value) {
                activityData = Number(activityMeta.activity_value);
              }

              let unit =
                q.unit ||
                q.answers?.unit ||
                q.answers?.activity_unit ||
                q.answers?.money_unit ||
                q.answers?.units_sold_unit ||
                q.answers?.[0]?.unit ||
                q.answers?.[0]?.activity_unit ||
                activityMeta.activity_unit ||
                '—';

              const co2 = Number(gases.co2 || 0);
              const ch4 = Number(gases.ch4 || 0);
              const n2o = Number(gases.n2o || 0);
              const co2e = Number(
                gases.co2e_total || normalizedEmission.co2e || 0,
              );
              if (co2e > 0) hasEmissions = true;
              const ef =
                normalizedEmission.emission_factor?.name ||
                (activityData > 0 ? (co2e / activityData).toFixed(6) : '—');
              const matchedUncertainty = category.uncertaintyMatrix?.find(
                (u: any) => u.questionId === q.id,
              );
              const uncertainty = Number(
                matchedUncertainty?.totalUncertaintyPercent || 0,
              );

              totalCO2 += co2;
              totalCH4 += ch4;
              totalN2O += n2o;
              totalCO2e += co2e;

              // Emissions-weighted uncertainty calculation
              if (co2e > 0) {
                if (uncertainty > 0) {
                  totalEmissionsForUncertainty += co2e;
                  const u = uncertainty / 100;
                  weightedVarianceSum += Math.pow(co2e * u, 2);
                } else {
                  missingUncertaintyCount++;
                }
              }

              return `
          <tr class="">
            <td class="px-3 py-2">${activity}</td>
            <td class="px-3 py-2">${unit}</td>
            <td class="px-3 py-2">${activityData}</td>
            <td class="px-3 py-2">${ef}</td>
            <td class="px-3 py-2">${co2.toFixed(4)}</td>
            <td class="px-3 py-2">${ch4.toFixed(6)}</td>
            <td class="px-3 py-2">${n2o.toFixed(6)}</td>
            <td class="px-3 py-2 font-semibold">${co2e.toFixed(4)}</td>
            <td class="px-3 py-2">${uncertainty.toFixed(2)}%</td>
          </tr>
        `;
            })
            .join('');
        })
        .join('');

      if (!hasEmissions) {
        return `<p class="text-sm text-gray-500 italic">${
          isEn
            ? 'This category has been excluded from the analysis because no activity data is available.'
            : "Questa categoria è stata esclusa dall'analisi in quanto non sono disponibili dati di attività."
        }</p>`;
      }
      // Emissions-weighted RSS: U = sqrt(Σ(E_i * u_i)²) / Σ(E_i) * 100
      let combinedUncertainty: string;
      if (missingUncertaintyCount > 0) {
        combinedUncertainty = 'N/A';
      } else if (totalEmissionsForUncertainty > 0) {
        const weightedRSS =
          (Math.sqrt(weightedVarianceSum) / totalEmissionsForUncertainty) * 100;
        combinedUncertainty = weightedRSS.toFixed(2);
      } else {
        combinedUncertainty = '0';
      }
      return `
      <table id="${categoryId}_table" class="w-full mb-4 border border-gray-300 text-sm">
        <thead>
          <tr class="bg-[#043B22] text-white text-left">
            <th class="px-3 py-2" style="width:17%">${isEn ? 'Activity description' : 'Descrizione attività'}</th>
            <th class="px-3 py-2" style="width:9%">${isEn ? 'Unit' : 'Unità'}</th>
            <th class="px-3 py-2" style="width:17%">${isEn ? 'Activity data' : 'Dati attività'}</th>
            <th class="px-3 py-2" style="width:17%">${isEn ? 'Emission factor' : 'Fattore di emissione'}</th>
            <th class="px-3 py-2" style="width:8%">CO₂ (kg)</th>
            <th class="px-3 py-2" style="width:8%">CH₄ (kg)</th>
            <th class="px-3 py-2" style="width:8%">N₂O (kg)</th>
            <th class="px-3 py-2" style="width:8%">CO₂e (kg)</th>
            <th class="px-3 py-2" style="width:8%">${isEn ? 'Uncertainty (%)' : 'Incertezza (%)'}</th>
          </tr>
        </thead>
        <tbody>
          ${rows}
          <tr class="bg-gray-100 font-semibold border-t">
            <td class="px-3 py-2">${isEn ? 'TOTAL' : 'TOTALE'}</td>
            <td></td><td></td><td></td>
            <td class="px-3 py-2">${totalCO2.toFixed(4)}</td>
            <td class="px-3 py-2">${totalCH4.toFixed(6)}</td>
            <td class="px-3 py-2">${totalN2O.toFixed(6)}</td>
            <td class="px-3 py-2">${totalCO2e.toFixed(4)}</td>
            <td class="px-3 py-2">${combinedUncertainty === 'N/A' ? 'N/A' : combinedUncertainty + '%'}</td>
          </tr>
        </tbody>
      </table>

      ${buildCategoryChartCanvas(categoryId)}
    `;
    };

    const categoryTotalsMap: Record<CategoryKey, number> = {
      category1: 0,
      category2: 0,
      category3: 0,
      category4: 0,
      category5: 0,
      category6: 0,
    };

    categories.forEach((cat: any) => {
      let catTotal = 0;

      cat?.questions?.forEach((q: any) => {
        (q.emissions || []).forEach((e: any) => {
          const normalized =
            typeof e === 'number'
              ? { co2e: e }
              : e?.response || e?.estimate || e || {};

          catTotal += Number(
            normalized?.constituent_gases?.co2e_total || normalized?.co2e || 0,
          );
        });
      });

      const key = cat.id as CategoryKey;

      if (key && key in categoryTotalsMap) {
        categoryTotalsMap[key] = catTotal / 1000; // kg → tonnes
      }
    });

    const categoryHasEmissions = (categoryId: string): boolean => {
      const category = categories.find((c: any) => c.id === categoryId);
      if (!category?.questions?.length) return false;

      return category.questions.some((q: any) =>
        (q.emissions || []).some((e: any) => {
          const normalized =
            typeof e === 'number'
              ? { co2e: e }
              : e?.response || e?.estimate || e || {};
          return (
            Number(
              normalized?.constituent_gases?.co2e_total ||
                normalized?.co2e ||
                0,
            ) > 0
          );
        }),
      );
    };

    const buildLSExclusionNote = (categoryId: string) => {
      const lsRaw = categoryLSMap[categoryId];
      const hasData = categoryHasEmissions(categoryId);

      // If LS >= 3 and has data, no exclusion note needed
      if (typeof lsRaw === 'number' && lsRaw >= 3 && hasData) {
        return '';
      }

      // No significance assessment completed
      if (lsRaw == null) {
        return `
          <p class="text-sm mb-3 italic text-gray-600">
            ${
              isEn
                ? 'This category has been excluded from the analysis because no significance assessment has been completed.'
                : "Questa categoria è stata esclusa dall'analisi in quanto non è stata completata la valutazione di significatività."
            }
          </p>
        `;
      }

      // LS below threshold
      if (lsRaw < 3) {
        const lsDisplay = lsRaw.toFixed(2);
        return `
          <p class="text-sm mb-3 italic text-gray-600">
            ${
              isEn
                ? 'This category has been excluded from the analysis because it has a Significance Level (LS) of ' +
                  lsDisplay +
                  ', which is below the materiality threshold.'
                : "Questa categoria è stata esclusa dall'analisi in quanto presenta un Livello di Significatività (LS) pari a " +
                  lsDisplay +
                  '.'
            }
          </p>
        `;
      }

      // Has LS >= 3 but no emissions data
      if (!hasData) {
        return `
          <p class="text-sm mb-3 italic text-gray-600">
            ${
              isEn
                ? 'This category has been excluded from the analysis because no activity data is available.'
                : "Questa categoria è stata esclusa dall'analisi in quanto non sono disponibili dati di attività."
            }
          </p>
        `;
      }

      return '';
    };

    const categorySummary: {
      categoryId: string;
      numItems: number;
      lsRaw: number | null;
      lsDisplay: number | '-';
    }[] = [];
    categories.forEach((cat: any) => {
      const catRows = this.significanceRows.filter(
        (r) => r.categoryId === cat.id,
      );

      const rowAverages: number[] = [];

      catRows.forEach((r) => {
        const scores = [
          r.criteria.magnitude,
          r.criteria.influence,
          r.criteria.riskOpportunity,
          r.criteria.stakeholderEngagement,
          r.criteria.dataAccessibility,
        ].map((v) => (v != null && v !== '-' ? Number(v) : null));

        const validScores = scores.filter((v) => v != null) as number[];
        if (validScores.length === 5) {
          rowAverages.push(validScores.reduce((a, b) => a + b, 0) / 5);
        }
      });

      let lsRaw: number | null = null;
      let lsDisplay: number | '-' = '-';

      if (rowAverages.length) {
        lsRaw = rowAverages.reduce((a, b) => a + b, 0) / rowAverages.length;
        lsDisplay = Number(lsRaw.toFixed(2));
      }

      categorySummary.push({
        categoryId: cat.id,
        numItems: rowAverages.length,
        lsRaw,
        lsDisplay,
      });
    });
    const categoryLSMap: Record<string, number | null> = {};
    categorySummary.forEach((c) => {
      categoryLSMap[c.categoryId] = c.lsRaw;
    });
    const includedCategories = categorySummary
      .filter((c) => typeof c.lsRaw === 'number' && c.lsRaw >= 3)
      .map((c) => c.categoryId);
    const categorySummaryTable = `
      <h2 class="text-lg font-semibold mb-2">${isEn ? 'Category significance summary' : 'Sintesi significatività per categoria'}</h2>

      <table class="w-full mb-4 border border-gray-300 text-sm">
        <thead>
          <tr class="bg-[#043B22] text-white text-left">
            <th class="px-4 py-2" style="width:35%">${isEn ? 'Category' : 'Categoria'}</th>
            <th class="px-4 py-2" style="width:35%">${isEn ? '# scored items' : 'N. elementi valutati'}</th>
            <th class="px-4 py-2" style="width:30%">LS</th>
          </tr>
        </thead>
        <tbody>
          ${categorySummary
            .map(
              (r) => `
              <tr class="">
                <td class="px-4 py-2">${this.formatCategoryId(r.categoryId)}</td>
                <td class="px-4 py-2">${r.numItems}</td>
                <td class="px-4 py-2 font-semibold">${typeof r.lsRaw === 'number' ? r.lsRaw.toFixed(2) : '-'}</td>
              </tr>
            `,
            )
            .join('')}
        </tbody>
      </table>
    `;

    const significantCategories = includedCategories;
    const overallUncertaintyPct = this.getInventoryOverallUncertaintyPct(
      significantCategories,
    );
    const grandTotals = this.calculateGrandTotals(
      categoryTotals,
      significantCategories,
    );
    const totalCO2_kg = grandTotals.co2;
    const totalCH4_kg = grandTotals.ch4;
    const totalN2O_kg = grandTotals.n2o;
    const totalCO2e_kg = grandTotals.co2e;
    const totalCO2_t = totalCO2_kg / 1000;
    const totalCH4_t = totalCH4_kg / 1000;
    const totalN2O_t = totalN2O_kg / 1000;
    const totalCO2e_t = totalCO2e_kg / 1000;
    const includedCategoryLabels = significantCategories
      .map((id) => this.formatCategoryId(id))
      .join(', ');
    categories = categories.map((cat: any) => ({
      ...cat,
      emissionSource: cat.emissionSource ?? '',
      dataSource: cat.dataSource ?? '',
    }));

    const buildTopicsReportedTable = () => {
      const rows = significantCategories
        .map((categoryId) => {
          const category = categories.find((c: any) => c.id === categoryId);
          if (!category) return '';

          // ---- Temi Materiali
          const temiMateriali = this.significanceRows
            .filter((r) => r.categoryId === categoryId)
            .map((r) => r.elemento)
            .join(', ');

          return `
          <tr class="">
            <td class="px-4 py-2 font-medium">
              ${this.formatCategoryId(categoryId)}
            </td>

            <td class="px-4 py-2">
              ${temiMateriali || '—'}
            </td>

            <!-- Emission source (stored per categoryId) -->
            <td class="px-4 py-2">
              <input
                type="text"
                class="w-full border px-2 py-1 text-sm"
                placeholder="${isEn ? 'Enter emission source' : 'Inserire fonte di emissione'}"
                value="${category.emissionSource}"
                oninput="window.updateEmissionSource('${categoryId}', this.value)"
              />
            </td>

                        <!-- Data source (future ready) -->
                        <td class="px-4 py-2">
              <input
                type="text"
                class="w-full border rounded px-2 py-1 text-sm"
                placeholder="${isEn ? 'Enter data source' : 'Inserire fonte dei dati'}"
                value="${category.dataSource}"
                oninput="window.updateDataSource('${categoryId}', this.value)"
              />
            </td>

          </tr>
        `;
        })
        .join('');

      if (!rows) {
        return `<p class="text-sm text-gray-500">
          ${isEn ? 'No significant categories identified.' : 'Nessuna categoria significativa identificata.'}
        </p>`;
      }

      return `
        <table class="w-full mb-6 border border-gray-300 text-sm">
          <thead>
            <tr class="bg-[#043B22] text-left text-white">
              <th class="px-4 py-2" style="width:10%">${isEn ? 'Category' : 'Categoria'}</th>
              <th class="px-4 py-2" style="width:40%">${isEn ? 'Material topics' : 'Temi materiali'}</th>
              <th class="px-4 py-2" style="width:25%">${isEn ? 'Emission source' : 'Fonte emissione'}</th>
              <th class="px-4 py-2" style="width:25%">${isEn ? 'Data source' : 'Fonte dei dati'}</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
        <div class="flex justify-end">
        <button
          class="px-4 py-2 bg-[#043B22] text-white rounded text-sm"
          onclick="window.saveEmissionSources()"
        >
          ${isEn ? 'Save' : 'Salva'}
        </button>
      </div>
      `;
    };

    return `
    <!-- Intro Text -->
    <p class="text-sm mb-3">
      ${
        isEn
          ? `The purpose of this chapter is to present the significance assessment and the quantified greenhouse gas emissions for each relevant activity. The significance matrix (presented in the table below) evaluates every potential emission source across five criteria: Entity, Influence, Risk/Opportunity, Employee Engagement and Data Availability. The average of these scores is the Significance Level (LS), and sources with LS ≥ 3 (or manually included by the user) are considered significant and are therefore included in the inventory.`
          : `Lo scopo di questo capitolo è presentare la valutazione della significatività e le emissioni di gas serra quantificate per ciascuna attività rilevante. La matrice di significatività riportata nella tabella seguente valuta ogni potenziale fonte di emissione secondo cinque criteri: Entità, Influenza, Rischio o Opportunità, Coinvolgimento dei dipendenti e Disponibilità dei dati. La media dei punteggi rappresenta il Livello di Significatività. Le fonti con Livello di Significatività maggiore o uguale a 3 oppure incluse manualmente sono considerate significative e quindi incluse nell'inventario.`
      }
    </p>

    <table class="w-full mb-4">
      <thead>
        <tr class="bg-[#043B22] text-left text-white">
          <th class="px-4 py-2" style="width:12%">${isEn ? 'Category' : 'Categoria'}</th>
          <th class="px-4 py-2" style="width:40%">${isEn ? 'Element' : 'Elemento'}</th>
          <th class="px-4 py-2" style="width:8%">${isEn ? 'EN' : 'EN'}</th>
          <th class="px-4 py-2" style="width:8%">${isEn ? 'INF' : 'INF'}</th>
          <th class="px-4 py-2" style="width:8%">${isEn ? 'RIS/OPP' : 'RIS/OPP'}</th>
          <th class="px-4 py-2" style="width:8%">${isEn ? 'IMP' : 'IMP'}</th>
          <th class="px-4 py-2" style="width:8%">${isEn ? 'DD' : 'DD'}</th>
          <th class="px-4 py-2" style="width:8%">${isEn ? 'LS' : 'LS'}</th>
        </tr>
      </thead>
      <tbody>
        ${this.significanceRows
          .map(
            (row) => `
          <tr class="">
            <td class="px-4 py-2">${this.formatCategoryId(row.categoryId)}</td>
            <td class="px-4 py-2">${row.elemento}</td>
            <td class="px-4 py-2">${row.criteria.magnitude || '-'}</td>
            <td class="px-4 py-2">${row.criteria.influence || '-'}</td>
            <td class="px-4 py-2">${row.criteria.riskOpportunity || '-'}</td>
            <td class="px-4 py-2">${row.criteria.stakeholderEngagement || '-'}</td>
            <td class="px-4 py-2">${row.criteria.dataAccessibility || '-'}</td>
            <td class="px-4 py-2 font-semibold">${row.ls || '-'}</td>
          </tr>
        `,
          )
          .join('')}
      </tbody>
    </table>

    <p class="text-sm mb-3">
        ${
          isEn
            ? `To make the results of the materiality matrix easier to read, the following table summarizes the assessment for each category. The average category score is calculated as the mean of the scores obtained by the elements within that category.`
            : `Per facilitare la lettura dei risultati della matrice di significatività, la tabella seguente riassume la valutazione per ciascuna categoria. Il punteggio medio di categoria è calcolato come media dei punteggi ottenuti dagli elementi appartenenti alla categoria.`
        }
    </p>

    ${categorySummaryTable}

    <h2 class="text-lg font-semibold mb-2">
        ${isEn ? '7.1 Choice of quantification approach' : '7.1 Scelta dell approccio di quantificazione'}
    </h2>

    <p class="text-sm mb-3">
        ${
          isEn
            ? `Emissions have been calculated using an activity‑based approach: for each activity, a measured or estimated value (the activity data) is multiplied by an emission factor sourced from recognised databases (e.g. DEFRA, IPCC or Climatiq). This method follows the GHG Protocol’s guidance on activity‑data‑based calculations. Where possible, activity data come from primary sources such as fuel invoices, energy meters, transport logs or procurement records. Emission factors are selected to match the technology, geography and time period of the activity. Purchased electricity is treated separately, using both location‑based and market‑based emission factors to reflect different accounting methods. All factors and data sources are documented in the calculator.`
            : `Le emissioni sono state calcolate utilizzando un approccio basato sui dati di attività. Per ogni attività un valore misurato o stimato viene moltiplicato per un fattore di emissione proveniente da database riconosciuti come DEFRA IPCC o Climatiq. Questo metodo segue le linee guida del GHG Protocol. Dove possibile i dati provengono da fonti primarie come fatture carburante contatori energetici registri di trasporto o documenti di acquisto. I fattori di emissione sono selezionati in base alla tecnologia alla localizzazione geografica e al periodo temporale. L'elettricità acquistata viene trattata separatamente utilizzando fattori basati sulla localizzazione e sul mercato.`
        }
    </p>

    <h2 class="text-lg font-semibold mb-2">
        ${isEn ? '7.2 Topics reported' : '7.2 Temi rendicontati'}
    </h2>

   <p class="text-sm mb-3">
      ${
        isEn
          ? `This inventory includes the following emission categories: ${includedCategoryLabels || '—'}.`
          : `Questo inventario include le seguenti categorie di emissione: ${includedCategoryLabels || '—'}.`
      }
    </p>

    ${buildTopicsReportedTable()}

    <h2 class="text-lg font-semibold mb-2">
        ${isEn ? '7.3 Category 1 – Direct emissions (stationary and mobile combustion)' : '7.3 Categoria 1 – Emissioni dirette da combustione fissa e mobile'}
    </h2>

    <p class="text-sm mb-3">
        ${
          isEn
            ? `Category 1 covers direct emissions from sources owned or controlled by the organisation. This includes combustion of fossil fuels in stationary equipment (boilers, generators) and mobile equipment (vehicles). The table below summarises the data for each activity, including the unit of measurement, activity data, emission factor applied, and the resulting emissions of CO₂, CH₄, N₂O and CO₂e. The associated uncertainty is also provided, derived from the Uncertainty Matrix.`
            : `La Categoria 1 comprende le emissioni dirette provenienti da fonti possedute o controllate dall'organizzazione. Include la combustione di combustibili fossili in apparecchiature fisse come caldaie e generatori e in veicoli. La tabella seguente riassume i dati per ciascuna attività compresa l'unità di misura i dati di attività il fattore di emissione applicato e le emissioni risultanti di CO2 CH4 N2O e CO2e. Viene inoltre riportato il livello di incertezza derivato dalla Matrice di Incertezza.`
        }
    </p>

      ${buildCategoryEmissionsTable('category1')}

    <h2 class="text-lg font-semibold mb-2">
        ${isEn ? '7.4 Category 2 – Indirect emissions from imported energy' : '7.4 Categoria 2 – Emissioni indirette da energia importata'}
    </h2>

    <p class="text-sm mb-3">
        ${
          isEn
            ? `Category 2 accounts for indirect emissions from the generation of purchased electricity, steam, heating or cooling that is consumed by the organisation. For electricity, both location‑based and market‑based results are provided. The table below summarises each imported energy activity.`
            : `La Categoria 2 riguarda le emissioni indirette derivanti dalla produzione di elettricità vapore riscaldamento o raffreddamento acquistati e consumati dall'organizzazione. Per l'elettricità vengono forniti risultati basati sulla localizzazione e sul mercato. La tabella seguente riassume ciascuna attività energetica importata.`
        }
    </p>

    ${buildCategoryEmissionsTable('category2')}

    <h2 class="text-lg font-semibold mb-2">
        ${isEn ? '7.5 Category 3 – Other indirect emissions (transport and travel)' : '7.5 Categoria 3 – Altre emissioni indirette da trasporto e viaggi'}
    </h2>

    <p class="text-sm mb-3">
        ${
          isEn
            ? `Category 3 includes indirect emissions arising from activities not owned or directly controlled by the organisation but occurring within its value chain. This category covers upstream and downstream transportation and distribution, employee commuting, client and visitor travel, and business travel. The table below summarises each relevant activity, including the unit of measurement, activity data, emission factors applied, and the resulting emissions of CO₂, CH₄, N₂O and CO₂e. The associated uncertainty is also reported, based on the Uncertainty Matrix.`
            : `La Categoria 3 include le emissioni indirette derivanti da attività non possedute o controllate direttamente dall organizzazione ma che si verificano all interno della sua catena del valore. Comprende trasporto e distribuzione a monte e a valle spostamenti dei dipendenti viaggi di clienti e visitatori e viaggi di lavoro. La tabella seguente riassume ciascuna attività rilevante includendo dati di attività fattori di emissione emissioni per gas totale CO2e e livello di incertezza.`
        }
    </p>

    ${buildCategoryEmissionsTable('category3')}

    <h2 class="text-lg font-semibold mb-2">
        ${isEn ? '7.6 Category 4 – Purchased goods, waste, and leased assets' : '7.6 Categoria 4 – Beni acquistati rifiuti e beni in leasing'}
    </h2>

    <p class="text-sm mb-3">
        ${
          isEn
            ? `Category 4 captures indirect emissions associated with purchased goods and services, capital goods, waste generation and disposal, leased assets, and other service-related activities. These emissions arise throughout the lifecycle of products and services procured by the organisation, including raw material extraction, manufacturing, transport, use, and end-of-life treatment. The table below presents the quantified emissions for each relevant activity, including activity data, emission factors, greenhouse gas emissions by gas, total CO₂e, and uncertainty levels.`
            : `La Categoria 4 copre le emissioni indirette associate all'acquisto di beni e servizi beni capitali generazione e smaltimento dei rifiuti beni in leasing e altre attività di servizio. Queste emissioni si verificano lungo il ciclo di vita dei prodotti e servizi acquistati inclusa l'estrazione delle materie prime la produzione il trasporto l'utilizzo e il trattamento a fine vita. La tabella seguente presenta le emissioni quantificate per ciascuna attività rilevante.`
        }
    </p>

    ${buildCategoryEmissionsTable('category4')}

    <h2 class="text-lg font-semibold mb-2">
        ${isEn ? '7.7 Category 5 – Downstream emissions and product impacts' : '7.7 Categoria 5 – Emissioni a valle e impatti dei prodotti'}
    </h2>

    <p class="text-sm mb-3">
        ${
          isEn
            ? `Category 5 accounts for downstream indirect emissions related to the use phase and end-of-life treatment of sold products, downstream leased assets, and financial investments. These emissions occur after the product leaves the organisation’s control and may include emissions generated during customer use, product disposal, or investment-linked activities. The table below summarises emissions by activity, detailing activity data, emission factors, emissions by greenhouse gas, total CO₂e, and associated uncertainty.`
            : `La Categoria 5 comprende le emissioni indirette a valle relative alla fase di utilizzo e al trattamento a fine vita dei prodotti venduti ai beni in leasing a valle e agli investimenti finanziari. Queste emissioni si verificano dopo che il prodotto lascia il controllo dell'organizzazione e possono includere le emissioni generate durante l'utilizzo da parte dei clienti lo smaltimento dei prodotti o le attività collegate agli investimenti.`
        }
    </p>

    ${buildCategoryEmissionsTable('category5')}

    <h2 class="text-lg font-semibold mb-2">
        ${isEn ? '7.8 Category 6 – Other indirect emissions' : '7.8 Categoria 6 – Altre emissioni indirette'}
    </h2>

    <p class="text-sm mb-3">
        ${
          isEn
            ? `Category 6 includes other indirect greenhouse gas emissions or removals that do not fall under Categories 3–5. This category ensures that any remaining material emission sources within the organisation’s value chain are captured and reported where relevant. The table below provides a summary of quantified emissions for these activities, including activity data, emission factors, emissions by gas, CO₂e totals, and uncertainty percentages.`
            : `La Categoria 6 include altre emissioni indirette di gas serra o rimozioni che non rientrano nelle Categorie da 3 a 5. Questa categoria garantisce che eventuali fonti di emissione materiali rimanenti nella catena del valore dell'organizzazione siano catturate e rendicontate ove rilevanti.`
        }
    </p>
    ${buildCategoryEmissionsTable('category6')}

    <h2 class="text-lg font-semibold mb-2">
        ${isEn ? 'Total emissions' : 'Emissioni totali'}
    </h2>

      <p class="text-sm mb-3">
        ${
          isEn
            ? `A summary of all emissions is provided below. Activity-level emissions are reported in kilograms (kg), while total emissions are presented in tonnes (t), obtained by dividing kilogram values by 1,000.`
            : `Di seguito è riportato un riepilogo delle emissioni. Le emissioni a livello di attività sono espresse in chilogrammi (kg), mentre i totali sono riportati in tonnellate (t), ottenute dividendo i valori in kg per 1.000.`
        }
    </p>

  <table class="w-full mt-4 mb-6 border border-gray-300 text-sm">
    <thead>
      <tr class="bg-[#043B22] text-white text-left">
        <th class="px-4 py-2" style="width:50%">${isEn ? 'Metric' : 'Metrica'}</th>
        <th class="px-4 py-2" style="width:50%">${isEn ? 'Value' : 'Valore'}</th>
      </tr>
    </thead>
    <tbody>
      <tr class="">
        <td class="px-4 py-2 font-medium">${isEn ? 'Total CO₂' : 'CO₂ Totale'}</td>
        <td class="px-4 py-2">${[totalCO2_t.toFixed(2)]} t</td>
      </tr>

      <tr class="">
        <td class="px-4 py-2 font-medium">${isEn ? 'Total CH₄' : 'CH₄ Totale'}</td>
        <td class="px-4 py-2">${[totalCH4_t.toFixed(4)]} t</td>
      </tr>

      <tr class="">
        <td class="px-4 py-2 font-medium">${isEn ? 'Total N₂O' : 'N₂O Totale'}</td>
        <td class="px-4 py-2">${[totalN2O_t.toFixed(4)]} t</td>
      </tr>

      <tr class="">
        <td class="px-4 py-2 font-medium">${isEn ? 'Total CO₂e' : 'CO₂e Totale'}</td>
        <td class="px-4 py-2">${[totalCO2e_t.toFixed(2)]} t CO₂e</td>
      </tr>

      <tr>
        <td class="px-4 py-2 font-medium">${isEn ? 'Overall uncertainty' : 'Incertezza complessiva'}</td>
        <td class="px-4 py-2">
          ${Number.isNaN(overallUncertaintyPct) ? `<span>N/A</span> <div class="text-xs italic text-gray-500 mt-1">${isEn ? 'Uncertainty missing for some included sources' : 'Incertezza mancante per alcune fonti incluse'}</div>` : `${overallUncertaintyPct.toFixed(2)} %`}
        </td>
      </tr>
    </tbody>
  </table>
      ${buildTotalEmissionsPieCanvas()}
  `;
  }

  private renderCategoryCharts(): void {
    const categories = JSON.parse(localStorage.getItem('categories') || '[]');
    const isEn = this.currentLanguage === 'english';

    setTimeout(() => {
      categories.forEach((category: any) => {
        const canvasId = `chart_${category.id}`;
        const canvas = document.getElementById(
          canvasId,
        ) as HTMLCanvasElement | null;

        if (!canvas) {
          return;
        }

        if (!category.questions?.length) {
          return;
        }

        // Build chart data
        const chartData: { label: string; emissions: number }[] =
          category.questions.map((q: any) => {
            let total = 0;

            (q.emissions || []).forEach((e: any) => {
              const normalized =
                typeof e === 'number'
                  ? { co2e: e }
                  : e?.response || e?.estimate || e || {};

              total += Number(
                normalized?.constituent_gases?.co2e_total ||
                  normalized?.co2e ||
                  0,
              );
            });

            return {
              label: q.title || q.id || '—',
              emissions: total / 1000,
            };
          });

        const labels = chartData.map((d: { label: string }) => d.label);
        const values = chartData.map((d: { emissions: number }) => d.emissions);

        // Remove zero values (log scale safety)
        const filtered: { label: string; value: number }[] = labels
          .map((label: string, i: number) => ({
            label,
            value: values[i],
          }))
          .filter((d: { label: string; value: number }) => d.value > 0);

        if (!filtered.length) {
          return;
        }

        const filteredLabels = filtered.map((d: { label: string }) => d.label);
        const filteredValues = filtered.map((d: { value: number }) => d.value);

        const top3 = [...filteredValues].sort((a, b) => b - a).slice(0, 3);

        // Wrap long labels
        const wrapLabel = (label: string, maxWidth = 40): string[] => {
          if (label.length <= maxWidth) return [label];
          const words = label.split(' ');
          const lines: string[] = [];
          let line = '';
          for (const word of words) {
            if ((line + ' ' + word).trim().length <= maxWidth) {
              line = (line + ' ' + word).trim();
            } else {
              lines.push(line);
              line = word;
            }
          }
          if (line) lines.push(line);
          return lines;
        };

        const wrappedLabels = filteredLabels.map((l: string) =>
          wrapLabel(l, 35),
        );

        // Destroy previous chart if exists
        Chart.getChart(canvas)?.destroy();

        // Render Chart.js bar chart
        new Chart(canvas, {
          type: 'bar',
          data: {
            labels: wrappedLabels,
            datasets: [
              {
                label: isEn ? 'CO₂ Emissions (tCO₂e)' : 'Emissioni CO₂ (tCO₂e)',
                data: filteredValues,
                backgroundColor: filteredValues.map((v: number) =>
                  top3.includes(v) ? '#043B22' : '#459470ff',
                ),
                borderRadius: 10,
                barThickness: 20,
                maxBarThickness: 30,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: 'y',
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => `${ctx.raw} ${isEn ? 'tCO₂e' : 'tCO₂e'}`,
                },
              },
            },
            scales: {
              x: {
                type: 'logarithmic',
                min: 0.0001,
                title: {
                  display: true,
                  text: isEn
                    ? 'CO₂ Emissions (tCO₂e)'
                    : 'Emissioni CO₂ (tCO₂e)',
                },
              },
              y: {
                ticks: {
                  padding: 8,
                  font: { size: 11 },
                  autoSkip: false,
                },
              },
            },
          },
        });
      });
    }, 500);
  }

  private renderTotalEmissionsPieChart(): void {
    const isEn = this.currentLanguage === 'english';
    const categories = JSON.parse(localStorage.getItem('categories') || '[]');

    setTimeout(() => {
      const canvas = document.getElementById(
        'emissionsPieChart',
      ) as HTMLCanvasElement | null;

      if (!canvas) {
        return;
      }

      // ---- Calculate LS per category to determine significant ones ----
      const categoryLSMap: Record<string, number | null> = {};
      categories.forEach((cat: any) => {
        const catRows = this.significanceRows.filter(
          (r: any) => r.categoryId === cat.id,
        );
        const rowAverages: number[] = [];

        catRows.forEach((r: any) => {
          const scores = [
            r.criteria.magnitude,
            r.criteria.influence,
            r.criteria.riskOpportunity,
            r.criteria.stakeholderEngagement,
            r.criteria.dataAccessibility,
          ].map((v: any) => (v != null && v !== '-' ? Number(v) : null));

          const validScores = scores.filter((v: any) => v != null) as number[];
          if (validScores.length === 5) {
            rowAverages.push(
              validScores.reduce((a: number, b: number) => a + b, 0) / 5,
            );
          }
        });

        if (rowAverages.length) {
          categoryLSMap[cat.id] =
            rowAverages.reduce((a: number, b: number) => a + b, 0) /
            rowAverages.length;
        } else {
          categoryLSMap[cat.id] = null;
        }
      });

      // ---- Determine significant categories (LS >= 3) ----
      const significantCategoryIds: string[] = categories
        .filter((c: any) => {
          const ls = categoryLSMap[c.id];
          return typeof ls === 'number' && ls >= 3;
        })
        .map((c: any) => c.id);

      // ---- Calculate totals only for significant categories ----
      const categoryEmissions: { id: string; label: string; value: number }[] =
        [];
      const colorPalette = [
        '#ACA13D',
        '#043b22a4',
        '#918629ff',
        '#aca13d7c',
        '#043B22',
        '#0d3322ff',
      ];

      categories.forEach((cat: any, index: number) => {
        if (!significantCategoryIds.includes(cat.id)) return;

        let sum = 0;
        cat?.questions?.forEach((q: any) => {
          (q.emissions || []).forEach((e: any) => {
            const normalized =
              typeof e === 'number'
                ? { co2e: e }
                : e?.response || e?.estimate || e || {};

            sum += Number(
              normalized?.constituent_gases?.co2e_total ||
                normalized?.co2e ||
                0,
            );
          });
        });

        const categoryNum = cat.id.replace('category', '');
        categoryEmissions.push({
          id: cat.id,
          label: isEn ? `Category ${categoryNum}` : `Categoria ${categoryNum}`,
          value: sum / 1000, // kg → tonnes
        });
      });

      if (!categoryEmissions.length) {
        return;
      }

      const labels = categoryEmissions.map((c) => c.label);
      const values = categoryEmissions.map((c) => c.value);
      const colors = categoryEmissions.map(
        (_, i) => colorPalette[i % colorPalette.length],
      );
      const totalSum = values.reduce((a, b) => a + b, 0);

      // Destroy old chart if exists
      Chart.getChart(canvas)?.destroy();

      // ✅ REAL CHART.JS CANVAS — NOT IMAGE
      new Chart(canvas, {
        type: 'pie',
        data: {
          labels,
          datasets: [
            {
              label: 'Emissions (tCO₂e)',
              data: values,
              backgroundColor: colors,
              hoverOffset: 6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            legend: {
              position: 'bottom',
              align: 'center',
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const value = ctx.raw as number;
                  const percent = totalSum
                    ? ((value / totalSum) * 100).toFixed(2)
                    : '0.00';

                  return `${ctx.label}: ${value.toFixed(2)} tCO₂e (${percent}%)`;
                },
              },
            },
          },
        },
      });
    }, 400);
  }

  private buildSection10Uncertainty(): string {
    const isEn = this.currentLanguage === 'english';

    const storedCategories = JSON.parse(
      localStorage.getItem('categories') || '[]',
    );

    // ---- Calculate LS per category (same logic as Chapter 7) ----
    const categoryLSMap: Record<string, number | null> = {};
    storedCategories.forEach((cat: any) => {
      const catRows = this.significanceRows.filter(
        (r: any) => r.categoryId === cat.id,
      );
      const rowAverages: number[] = [];

      catRows.forEach((r: any) => {
        const scores = [
          r.criteria.magnitude,
          r.criteria.influence,
          r.criteria.riskOpportunity,
          r.criteria.stakeholderEngagement,
          r.criteria.dataAccessibility,
        ].map((v: any) => (v != null && v !== '-' ? Number(v) : null));

        const validScores = scores.filter((v: any) => v != null) as number[];
        if (validScores.length === 5) {
          rowAverages.push(
            validScores.reduce((a: number, b: number) => a + b, 0) / 5,
          );
        }
      });

      if (rowAverages.length) {
        categoryLSMap[cat.id] =
          rowAverages.reduce((a: number, b: number) => a + b, 0) /
          rowAverages.length;
      } else {
        categoryLSMap[cat.id] = null;
      }
    });

    // ---- Determine significant categories (LS >= 3) ----
    const significantCategoryIds: string[] = storedCategories
      .filter((c: any) => {
        const ls = categoryLSMap[c.id];
        return typeof ls === 'number' && ls >= 3;
      })
      .map((c: any) => c.id);

    type UncertaintyRow = {
      category: string;
      dqAvg: string;
      efAvg: string;
      combined: string;
      co2eKg: number;
      notes: string;
    };

    const rows: UncertaintyRow[] = [];
    let totalInventoryEmissions = 0;
    let inventoryWeightedVarianceSum = 0;
    let hasMissingUncertainty = false;

    // Only process significant categories
    storedCategories.forEach((cat: any) => {
      if (!significantCategoryIds.includes(cat.id)) return;
      if (
        !Array.isArray(cat?.uncertaintyMatrix) ||
        !cat.uncertaintyMatrix.length
      )
        return;
      if (!Array.isArray(cat?.questions)) return;

      const activities = cat.uncertaintyMatrix;

      // Calculate emissions-weighted uncertainty for this category
      let categoryEmissions = 0;
      let categoryWeightedVarianceSum = 0;
      let categoryMissingUncertainty = false;

      cat.questions.forEach((q: any) => {
        const emissionsArray = Array.isArray(q.emissions) ? q.emissions : [];
        if (!emissionsArray.length) return;

        let questionCo2eKg = 0;
        for (const e of emissionsArray) {
          const normalized =
            typeof e === 'number'
              ? { co2e: e, constituent_gases: { co2e_total: e } }
              : e?.response || e?.estimate || e || {};
          const co2eKg = Number(
            normalized?.constituent_gases?.co2e_total ?? normalized?.co2e ?? 0,
          );
          questionCo2eKg += co2eKg;
        }

        if (questionCo2eKg <= 0) return;

        const matched = activities.find((u: any) => u.questionId === q.id);
        const uncPct = Number(matched?.totalUncertaintyPercent);

        if (!isFinite(uncPct) || uncPct <= 0) {
          categoryMissingUncertainty = true;
          hasMissingUncertainty = true;
          return;
        }

        categoryEmissions += questionCo2eKg;
        const u = uncPct / 100;
        categoryWeightedVarianceSum += Math.pow(questionCo2eKg * u, 2);

        // Also accumulate for overall inventory
        totalInventoryEmissions += questionCo2eKg;
        inventoryWeightedVarianceSum += Math.pow(questionCo2eKg * u, 2);
      });

      // Calculate category combined uncertainty using emissions-weighted RSS
      let combinedUncertainty: string;
      if (categoryMissingUncertainty) {
        combinedUncertainty = 'N/A';
      } else if (categoryEmissions > 0) {
        const weightedRSS =
          (Math.sqrt(categoryWeightedVarianceSum) / categoryEmissions) * 100;
        combinedUncertainty = weightedRSS.toFixed(1);
      } else {
        combinedUncertainty = '0';
      }

      // Average DQ and EF scores for display (these are still simple averages for informational purposes)
      const avgDQ =
        activities.reduce(
          (sum: number, a: any) => sum + (a.uncADPercent ?? 0),
          0,
        ) / activities.length;
      const avgEF =
        activities.reduce(
          (sum: number, a: any) => sum + (a.uncEFPercent ?? 0),
          0,
        ) / activities.length;

      rows.push({
        category: cat.id,
        dqAvg: avgDQ.toFixed(1),
        efAvg: avgEF.toFixed(1),
        combined: combinedUncertainty,
        co2eKg: categoryEmissions,
        notes: activities?.[0]?.notes?.join(', ') || '',
      });
    });

    if (!rows.length) {
      return `<p class="text-sm text-gray-500">
        ${isEn ? 'No uncertainty data available for significant categories.' : 'Nessun dato di incertezza disponibile per le categorie significative.'}
      </p>`;
    }

    // Calculate overall inventory uncertainty using emissions-weighted RSS (GHG Protocol compliant)
    let overallUncertainty: string;
    if (hasMissingUncertainty) {
      overallUncertainty = 'N/A';
    } else if (totalInventoryEmissions > 0) {
      const weightedRSS =
        (Math.sqrt(inventoryWeightedVarianceSum) / totalInventoryEmissions) *
        100;
      overallUncertainty = weightedRSS.toFixed(1);
    } else {
      overallUncertainty = '0';
    }

    // Interpretation: lower uncertainty = more precise/reliable data
    const uncValue = Number(overallUncertainty);
    const interpretationEn = isNaN(uncValue)
      ? 'cannot be determined'
      : uncValue <= 10
        ? 'highly reliable'
        : uncValue <= 20
          ? 'reliable'
          : uncValue <= 30
            ? 'moderately reliable'
            : 'approximate';

    const interpretationIt = isNaN(uncValue)
      ? 'non determinabile'
      : uncValue <= 10
        ? 'molto affidabili'
        : uncValue <= 20
          ? 'affidabili'
          : uncValue <= 30
            ? 'moderatamente affidabili'
            : 'approssimativi';

    return `
      <div class="">

        <p class="text-sm">
          ${
            isEn
              ? `The reliability of this GHG inventory depends on the quality of input data and emission factors.To evaluate confidence levels, each activity was assessed across two dimensions:`
              : `L’affidabilità di questo inventario GHG dipende dalla qualità dei dati di input e dei fattori di emissione.Per valutare i livelli di confidenza, ogni attività è stata analizzata su due dimensioni:`
          }
        </p>

        <ul class="list-disc pl-6 text-sm">
          <li>
            <strong>${isEn ? 'Data quality' : 'Qualità dei dati'}</strong> –
            ${isEn ? 'whether the activity data come from direct measurements, reliable invoices or estimates; scored on a 1–5 scale (1 = very good, 5 = very poor).' : 'se i dati provengono da misurazioni dirette, fatture affidabili o stime; punteggio su scala 1–5 (1 = molto buono, 5 = molto scarso).'}
          </li>
          <li>
            <strong>${isEn ? 'Emission-factor reliability' : 'Affidabilità del fattore di emissione'}</strong> –
            ${isEn ? 'whether the emission factor is specific to the region, sector and technology, or is a generic proxy; scored 1–5 (1 = very good, 5 = very poor).' : 'se il fattore di emissione è specifico per regione, settore e tecnologia, o un proxy generico; punteggio 1–5 (1 = molto buono, 5 = molto scarso).'}
          </li>
        </ul>

        <p class="text-sm">
          ${
            isEn
              ? `These scores were mapped to percentage uncertainty using predefined ranges (e.g. averages ≤ 1.4 → 2 %, ≤ 1.9 → 5 %, up to > 4.4 → 50 %). The combined uncertainty for each category was calculated using the GHG Protocol emissions-weighted root-sum-of-squares (RSS) method: U = √(Σ(E<sub>i</sub> × u<sub>i</sub>)²) / Σ(E<sub>i</sub>) × 100, where E<sub>i</sub> is the emission from each activity and u<sub>i</sub> is the corresponding uncertainty. Only significant categories (LS ≥ 3) are included. The following table summarises the results:`
              : `Questi punteggi sono stati convertiti in percentuali di incertezza utilizzando intervalli predefiniti (ad es. medie ≤ 1,4 → 2 %, ≤ 1,9 → 5 %, fino a > 4,4 → 50 %). L'incertezza combinata per ciascuna categoria è stata calcolata utilizzando il metodo della radice quadrata della somma dei quadrati (RSS) ponderata per emissioni del GHG Protocol: U = √(Σ(E<sub>i</sub> × u<sub>i</sub>)²) / Σ(E<sub>i</sub>) × 100, dove E<sub>i</sub> è l'emissione di ciascuna attività e u<sub>i</sub> è l'incertezza corrispondente. Sono incluse solo le categorie significative (LS ≥ 3). La tabella seguente riassume i risultati:`
          }
        </p>

        <table class="w-full mb-4 border border-gray-300 text-sm">
          <thead>
            <tr class="bg-[#043B22] text-white text-left">
              <th class="px-4 py-2">${isEn ? 'Category' : 'Categoria'}</th>
              <th class="px-4 py-2">${isEn ? 'Data quality (%)' : 'Qualità dati (%)'}</th>
              <th class="px-4 py-2">${isEn ? 'Emission factor (%)' : 'Fattore emissione (%)'}</th>
              <th class="px-4 py-2">${isEn ? 'Combined uncertainty (%)' : 'Incertezza combinata (%)'}</th>
              <th class="px-4 py-2">${isEn ? 'Notes' : 'Note'}</th>
            </tr>
          </thead>

          <tbody>
            ${rows
              .map(
                (r: UncertaintyRow) => `
              <tr class="">
                <td class="px-4 py-2">${this.formatCategoryId(r.category)}</td>
                <td class="px-4 py-2">${r.dqAvg}</td>
                <td class="px-4 py-2">${r.efAvg}</td>
                <td class="px-4 py-2 font-semibold">${r.combined}${r.combined !== 'N/A' ? ' %' : ''}</td>
                <td class="px-4 py-2">${r.notes || '-'}</td>
              </tr>
            `,
              )
              .join('')}
          </tbody>
        </table>

        <p class="text-sm">
          ${
            isEn
              ? `Uncertainty below 10% indicates highly reliable data; 10–20% indicates reliable data; 20–30% indicates moderately reliable data; above 30% indicates approximate data. Overall, the inventory's emissions-weighted uncertainty is <strong>${overallUncertainty}${overallUncertainty !== 'N/A' ? '%' : ''}</strong>, which suggests that the reported figures are <strong>${interpretationEn}</strong>.`
              : `Un'incertezza inferiore al 10% indica dati molto affidabili; 10–20% indica dati affidabili; 20–30% indica dati moderatamente affidabili; superiore al 30% indica dati approssimativi. Complessivamente, l'incertezza ponderata per emissioni dell'inventario è <strong>${overallUncertainty}${overallUncertainty !== 'N/A' ? '%' : ''}</strong>, il che suggerisce che i valori riportati sono <strong>${interpretationIt}</strong>.`
          }
        </p>
      </div>
    `;
  }

  private injectSection10Content() {
    const section10 = this.sections.find((s) => s.id === 10);
    if (!section10) return;

    const generated = this.buildSection10Uncertainty();

    // Avoid overwriting user edits
    if (!section10.content || section10.content.trim() === '') {
      section10.content = generated;
      section10.isEditing = false;
      this.saveSectionsToLocal();
    }
  }

  private buildSection6Boundaries(): string {
    const isEn = this.currentLanguage === 'english';

    const name = this.companyDetails.companyName || '[CompanyName]';
    const { city: parsedCity, formattedAddress: parsedAddr } =
      parseCompanyAddress(this.companyDetails.address);
    const address = parsedAddr || '[HeadquartersAddress]';

    const categories = JSON.parse(localStorage.getItem('categories') || '[]');
    // STEP 3 – Calculate average LS per category
    const categoryLSMap: Record<string, number | null> = {};

    categories.forEach((cat: any) => {
      const rows = this.significanceRows.filter((r) => r.categoryId === cat.id);

      const rowAverages: number[] = [];

      rows.forEach((r) => {
        const scores = [
          r.criteria.magnitude,
          r.criteria.influence,
          r.criteria.riskOpportunity,
          r.criteria.stakeholderEngagement,
          r.criteria.dataAccessibility,
        ].map((v) => (v != null && v !== '-' ? Number(v) : null));

        const validScores = scores.filter((v) => v != null) as number[];

        if (validScores.length === 5) {
          rowAverages.push(validScores.reduce((a, b) => a + b, 0) / 5);
        }
      });

      if (rowAverages.length) {
        categoryLSMap[cat.id] =
          rowAverages.reduce((a, b) => a + b, 0) / rowAverages.length;
      } else {
        categoryLSMap[cat.id] = null;
      }
    });

    // STEP 4 – Check if a category actually has emissions
    const categoryHasEmissions = (category: any): boolean => {
      return category?.questions?.some((q: any) =>
        (q.emissions || []).some((e: any) => {
          const normalized =
            typeof e === 'number'
              ? { co2e: e }
              : e?.response || e?.estimate || e || {};

          return (
            Number(
              normalized?.constituent_gases?.co2e_total ||
                normalized?.co2e ||
                0,
            ) > 0
          );
        }),
      );
    };

    // STEP 5 – Determine included and excluded categories
    const included: number[] = [];
    const excluded: number[] = [];

    categories.forEach((category: any) => {
      const ls = categoryLSMap[category.id];
      const hasEmissions = categoryHasEmissions(category);

      const categoryNumber = Number(category.id.replace('category', ''));

      if (ls != null && ls >= 3 && hasEmissions) {
        included.push(categoryNumber);
      } else {
        excluded.push(categoryNumber);
      }
    });

    const includedText = included.length
      ? included.join(', ')
      : isEn
        ? 'None'
        : 'Nessuna';
    const excludedText = excluded.length
      ? excluded.join(', ')
      : isEn
        ? 'None'
        : 'Nessuna';

    return `
    <div class="">

      <!--p class="text-sm">
        ${
          isEn
            ? `In the context of greenhouse gas (GHG) emissions management and compliance with international best practices,
              <strong>${name}</strong> adopts structured criteria to define inventory boundaries.`
            : `Nel contesto della gestione delle emissioni di gas serra (GHG) e della conformità alle migliori pratiche internazionali,
              <strong>${name}</strong> adotta criteri strutturati per la definizione dei confini dell’inventario.`
        }
      </p-->

      <h3 class="text-base font-bold">6.1. ${isEn ? 'Organisational boundaries' : 'Confini organizzativi'}</h3>

      <p class="text-sm">
        ${
          isEn
            ? `ISO 14064‑1 recognises two main approaches to establishing organisational boundaries: <strong>control</strong> and <strong>equity</strong> share.`
            : `ISO 14064-1 riconosce due principali approcci per definire i confini organizzativi:
              <strong>controllo</strong> ed <strong>equity</strong> share.`
        }
      </p>

      <ul class="list-disc pl-6 text-sm">
        <li>
          <strong>${isEn ? 'Control approach' : 'Approccio di controllo'}:</strong>
          ${
            isEn
              ? `the organisation accounts for all quantified GHG emissions and removals from installations over which it has financial or operational control.`
              : `l'organizzazione tiene conto di tutte le emissioni e rimozioni quantificate di gas serra provenienti da impianti sui quali ha un controllo finanziario o operativo.`
          }
        </li>
        <li>
          <strong>${isEn ? 'Equity share approach' : 'Approccio equity share'}:</strong>
          ${
            isEn
              ? `the organisation accounts for its proportional share of GHG emissions and removals from jointly owned installations.`
              : `l'organizzazione tiene conto della sua quota proporzionale di emissioni e rimozioni di gas serra provenienti da impianti di proprietà congiunta.`
          }
        </li>
      </ul>

      <p class="text-sm">
        ${
          isEn
            ? `<strong>${name}</strong> has adopted the <span class="highlight">[ControlApproachType]</span> approach to define its organisational boundaries, for reasons related to its size, operations and management structure. Under this approach, <strong>${name}</strong> accounts for emissions from all facilities over which it has <span class="highlight">[ControlType]</span> control and has authority to implement policies at the operational level. Entities or activities outside this boundary are not included in the inventory.`
            : `<strong>${name}</strong> ha adottato l’approccio <span class="highlight">[ControlApproachType]</span> per definire i propri confini organizzativi, per ragioni legate alle dimensioni, alle operazioni e alla struttura gestionale. In base a questo approccio, <strong>${name}</strong> contabilizza le emissioni provenienti da tutte le strutture sulle quali esercita un controllo <span class="highlight">[ControlType]</span> e ha l’autorità di implementare politiche a livello operativo. Le entità o le attività al di fuori di questo perimetro non sono incluse nell’inventario.`
        }
      </p>

      <h3 class="text-base font-bold">6.2. ${isEn ? 'Reporting boundaries' : 'Confini di rendicontazione'}</h3>

      <p class="text-sm">
        ${
          isEn
            ? `Reporting boundaries define which categories of emissions are included and the geographic scope. <strong>${name}</strong>’s reporting boundaries encompass operations at <strong>${address}</strong> and any other sites over which the company has control. This delimitation allows the company to identify, quantify and manage emissions generated directly by activities at these locations. Categories included in this report are <strong>${includedText}</strong>, while <strong>${excludedText}</strong> are excluded for reasons such as immateriality or lack of data.`
            : `I confini di rendicontazione definiscono quali categorie di emissioni sono incluse e l’ambito geografico. I confini di rendicontazione di <strong>${name}</strong> comprendono le operazioni presso <strong>${address}</strong> e qualsiasi altro sito sul quale l’azienda esercita il controllo. Questa delimitazione consente all’azienda di identificare, quantificare e gestire le emissioni generate direttamente dalle attività svolte in queste sedi. Le categorie incluse in questo report sono <strong>${includedText}</strong>, mentre <strong>${excludedText}</strong> sono escluse per motivi quali l’irrilevanza o la mancanza di dati.`
        }
      </p>

      <h3 class="text-base font-bold">6.3. ${isEn ? 'Categorisation of GHG emissions' : 'Categorizzazione delle emissioni GHG'}</h3>
      <p>${isEn ? 'Following ISO 14064‑1 and the GHG Protocol, emissions are categorised as follows:' : 'Secondo la norma ISO 14064-1 e il protocollo GHG, le emissioni sono classificate come segue:'}</p>

      <ol class="list-decimal pl-6 text-sm">
        <li><strong>${isEn ? 'Category 1' : 'Categoria 1'} -</strong> ${isEn ? '<strong>Direct emissions and removals :</strong> emissions from sources owned or controlled by the organisation, such as stationary combustion (boilers, generators) and mobile sources (vehicles).' : "<strong>Emissioni dirette e rimozioni :</strong> emissioni da fonti possedute o controllate dall'organizzazione, come combustione stazionaria (caldaie, generatori) e fonti mobili (veicoli)."}</li>
        <li><strong>${isEn ? 'Category 2' : 'Categoria 2'} -</strong> ${isEn ? '<strong>Indirect emissions from imported energy :</strong> emissions from the generation of purchased electricity, steam, heat or cooling consumed by the organisation.' : "<strong>Emissioni indirette da energia importata :</strong> emissioni dalla generazione di elettricità, vapore, calore o raffreddamento acquistati e consumati dall'organizzazione."}</li>
        <li><strong>${isEn ? 'Category 3' : 'Categoria 3'} -</strong> ${isEn ? '<strong>Indirect emissions from transport :</strong> emissions from upstream and downstream transport, including third‑party freight and business travel.' : '<strong>Emissioni indirette da trasporto :</strong> emissioni da trasporto a monte e a valle, incluso trasporto merci di terzi e viaggi di lavoro.'}</li>
        <li><strong>${isEn ? 'Category 4' : 'Categoria 4'} -</strong> ${isEn ? '<strong>Indirect emissions from products used by the organisation:</strong> emissions from the production of purchased goods, capital goods and services, as well as waste disposal.' : "<strong>Emissioni indirette da prodotti utilizzati dall'organizzazione :</strong> emissioni dalla produzione di beni acquistati, beni strumentali e servizi, nonché smaltimento dei rifiuti."}</li>
        <li><strong>${isEn ? 'Category 5' : 'Categoria 5'} -</strong> ${isEn ? '<strong>Indirect emissions associated with the use of products from the organisation :</strong> emissions during the use phase, downstream leased assets, end‑of‑life and investments.' : "<strong>Emissioni indirette associate all'uso dei prodotti dell'organizzazione :</strong> emissioni durante la fase di utilizzo, beni in leasing a valle, fine vita e investimenti."}</li>
        <li><strong>${isEn ? 'Category 6' : 'Categoria 6'} -</strong> ${isEn ? '<strong>Indirect emissions from other sources :</strong> any remaining indirect emissions, including land use, land‑use change, and custom sources defined by the user.' : "<strong>Emissioni indirette da altre fonti :</strong> tutte le restanti emissioni indirette, incluso uso del suolo, cambiamento di uso del suolo e fonti personalizzate definite dall'utente."}</li>
      </ol>

      <h3 class="text-base font-bold">6.4. ${isEn ? 'Principles adopted' : 'Principi adottati'}</h3>
      <p class="text-sm">
        ${
          isEn
            ? `The key principles adopted in defining <strong>${name}</strong>’s GHG inventory are as follows:`
            : `I principi chiave adottati per definire l’inventario GHG di <strong>${name}</strong> sono i seguenti:`
        }
      </p>
      ${
        isEn
          ? `<ul>
      <li><strong>General : </strong> These principles guide how the inventory is compiled to ensure GHG information is reported in a fair and correct manner.</li>
      <li><strong>Relevance : </strong> The inventory includes GHG sources, data, and methodologies that are appropriate to the needs of the intended users.</li>
      <li><strong>Materiality : </strong> Indirect emissions are selected based on their importance to decision-making for the organisation and its stakeholders (e.g., customers, suppliers, investors, public bodies, NGOs), either individually or when combined with other sources.</li>
      <li><strong>Completeness : </strong> All relevant GHG emissions and removals within the defined organisational and reporting boundaries are quantified and reported.</li>
      <li><strong>Consistency : </strong> Consistent calculation methods are applied over time to enable meaningful comparisons. Any changes in methodology are documented and justified.</li>
      <li><strong>Accuracy : </strong> Uncertainties in data and results are reduced as far as practicable, and assumptions are documented.</li>
      <li><strong>Transparency : </strong> Sufficient information is provided to allow third parties to understand the boundaries, methods, data sources, and the integrity of the inventory with reasonable confidence.</li>
      </ul>`
          : `<ul>
      <li><strong>Generale : </strong> questi principi guidano la compilazione dell’inventario per garantire che le informazioni sui gas a effetto serra siano rendicontate in modo corretto e adeguato.</li>
      <li><strong>Rilevanza : </strong> l’inventario include fonti di emissione, dati e metodologie coerenti con gli obiettivi dell’inventario e con le esigenze degli utilizzatori previsti.</li>
      <li><strong>Materialità : </strong> le emissioni indirette vengono selezionate in base alla loro importanza ai fini decisionali per l’organizzazione e i suoi stakeholder (es. clienti, fornitori, investitori, enti pubblici, ONG), sia singolarmente sia in combinazione con altre fonti.</li>
      <li><strong>Completezza : </strong> tutte le emissioni e gli assorbimenti rilevanti rientranti nei confini organizzativi e di rendicontazione definiti vengono quantificati e riportati.</li>
      <li><strong>Coerenza : </strong> vengono applicati metodi di calcolo coerenti nel tempo per consentire confronti significativi. Eventuali modifiche metodologiche sono documentate e motivate.</li>
      <li><strong>Accuratezza : </strong> le incertezze sui dati e sui risultati vengono ridotte per quanto possibile; ipotesi e stime sono documentate.</li>
      <li><strong>Trasparenza : </strong> vengono fornite informazioni sufficienti per consentire a terze parti di comprendere confini, metodi, fonti dati e l’integrità complessiva dell’inventario con un livello ragionevole di confidenza.</li>
      </ul>`
      }

      <h3 class="text-base font-bold">6.5. ${isEn ? 'GHG reporting phases' : 'Fasi di rendicontazione GHG'}</h3>
        ${
          isEn
            ? `<p class="text-sm">The GHG reporting process includes the following phases: </p>
        <ul>
        <li><strong>planning</strong> (defining boundaries and data collection)</li>
        <li><strong>calculation</strong> (applying emission factors)</li>
        <li><strong>validation</strong> (verifying data quality and uncertainty)</li>
        <li><strong>reporting</strong> (drafting this document)</li>
        <li><strong>improvement</strong> (identifying reduction actions)</li></ul>`
            : `<p class="text-sm">Il processo di rendicontazione dei GHG comprende le seguenti fasi:</p>
        <ul>
        <li><strong>pianificazione</strong> (definizione dei confini e raccolta dei dati)</li>
        <li><strong>calcolo</strong> (applicazione dei fattori di emissione)</li>
        <li><strong>validazione</strong> (verifica della qualità dei dati e dell’incertezza)</li>
        <li><strong>rendicontazione</strong> (redazione di questo documento)</li>
        <li><strong>miglioramento</strong> (identificazione delle azioni di riduzione)</li></ul>`
        }


      <h3 class="text-base font-bold">6.6. ${isEn ? 'Definition of significance' : 'Definizione della significatività'}</h3>
      <p class="text-sm">
        ${
          isEn
            ? `The assessment of the significance of greenhouse gas emissions and removals is based on the five criteria defined in <strong>Appendix H of ISO 14064-1</strong>:
            <br><strong>Entity (EN)</strong> – whether indirect emissions or removals are quantitatively substantial;
            <br><strong>Level of influence (INF)</strong> – the extent to which the organisation can monitor and reduce these emissions (e.g. through energy efficiency, eco-design, customer engagement);
            <br><strong>Risk/Opportunity (RIS/OPP)</strong> – whether the source exposes the organisation to climate-related risks (financial, regulatory, supply-chain, reputational) or creates opportunities for new markets or business models;
            <br><strong>Employee engagement (IMP)</strong> – the potential to involve employees in reducing emissions through incentives, car-pooling, internal carbon pricing, etc.;
            <br><strong>Data availability (DD)</strong> – the availability, completeness and reliability of data on the source.
            <br><br>Each criterion is scored from <strong>1 (negligible)</strong> to <strong>4 (considerable)</strong>.
            The <strong>significance level (LS)</strong> is calculated as the average of the five scores:
            <br><strong>LS = (EN + INF + RIS/OPP + IMP + DD) / 5</strong>.
            <br><br>A source is considered <strong>significant</strong> and included in the inventory if <strong>LS ≥ 3</strong>. Users may override inclusion based on <strong>professional judgement</strong>.`
            : `La valutazione della significatività delle emissioni e delle rimozioni di gas serra si basa sui cinque criteri definiti nell’<strong>Appendice H della norma ISO 14064-1</strong>:
            <br><strong>Entità (EN)</strong> – se le emissioni o le rimozioni indirette sono quantitativamente rilevanti;
            <br><strong>Livello di influenza (INF)</strong> – il grado in cui l’organizzazione può monitorare e ridurre tali emissioni (ad esempio tramite efficienza energetica, eco-design, coinvolgimento dei clienti);
            <br><strong>Rischio/Opportunità (RIS/OPP)</strong> – se la fonte espone l’organizzazione a rischi legati al clima (finanziari, normativi, della catena di approvvigionamento, reputazionali) o crea opportunità per nuovi mercati o modelli di business;
            <br><strong>Coinvolgimento dei dipendenti (IMP)</strong> – il potenziale di coinvolgere i dipendenti nella riduzione delle emissioni attraverso incentivi, car-pooling, carbon pricing interno, ecc.;
            <br><strong>Disponibilità dei dati (DD)</strong> – la disponibilità, completezza e affidabilità dei dati relativi alla fonte.
            <br><br>Ogni criterio è valutato con un punteggio da <strong>1 (trascurabile)</strong> a <strong>4 (considerevole)</strong>.
            Il <strong>livello di significatività (LS)</strong> è calcolato come la media dei cinque punteggi:
            <br><strong>LS = (EN + INF + RIS/OPP + IMP + DD) / 5</strong>.
            <br><br>Una fonte è considerata <strong>significativa</strong> ed è inclusa nell’inventario se <strong>LS ≥ 3</strong>. Gli utenti possono modificare l’inclusione sulla base del <strong>giudizio professionale</strong>.`
        }
        </p>

              <p class="text-sm mt-3">
        ${
          isEn
            ? `The following tables explain the <strong>Significance Level (LS)</strong> scoring system and the interpretation of each evaluation criterion.`
            : `Le seguenti tabelle spiegano il sistema di punteggio del <strong>Livello di Significatività (LS)</strong> e l’interpretazione di ciascun criterio di valutazione.`
        }
      </p>

      <!-- SIGNIFICANCE LEVEL SCALE TABLE -->
      <table class="w-full mb-4 mt-3">
        <thead>
          <tr class="bg-[#043B22] text-left text-white">
            <th class="px-4 py-2" style="width:50%">
              ${isEn ? 'Significance Level' : 'Livello di Significatività'}
            </th>
            <th class="px-4 py-2" style="width:50%">
              ${isEn ? 'α Level' : 'Livello α'}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr class="">
            <td class="px-4 py-2">${isEn ? 'Negligible' : 'Trascurabile'}</td>
            <td class="px-4 py-2">1</td>
          </tr>

          <tr class="">
            <td class="px-4 py-2">${isEn ? 'Low' : 'Basso'}</td>
            <td class="px-4 py-2">2</td>
          </tr>

          <tr class="">
            <td class="px-4 py-2">${isEn ? 'Significant' : 'Rilevante'}</td>
            <td class="px-4 py-2">3</td>
          </tr>

          <tr class="">
            <td class="px-4 py-2">${isEn ? 'Considerable' : 'Considerevole'}</td>
            <td class="px-4 py-2">4</td>
          </tr>
        </tbody>
      </table>


      <!-- CRITERIA INTERPRETATION TABLE -->
      <table class="w-full mb-4">
        <thead>
          <tr class="bg-[#043B22] text-left text-white">
            <th class="px-4 py-2" style="width:20%">
              ${isEn ? 'Criteria' : 'Criteri'}
            </th>
            <th class="px-4 py-2" style="width:20%">${isEn ? 'Negligible (1)' : 'Trascurabile (1)'}</th>
            <th class="px-4 py-2" style="width:20%">${isEn ? 'Low (2)' : 'Basso (2)'}</th>
            <th class="px-4 py-2" style="width:20%">${isEn ? 'Significant (3)' : 'Rilevante (3)'}</th>
            <th class="px-4 py-2" style="width:20%">${isEn ? 'Considerable (4)' : 'Considerevole (4)'}</th>
          </tr>
        </thead>

        <tbody>
          <tr class="">
            <td class="px-4 py-2 font-medium">${isEn ? 'Entity (EN)' : 'Entità (EN)'}</td>
            <td class="px-4 py-2">${isEn ? 'Emissions < 1% of total' : 'Emissioni < 1% del totale'}</td>
            <td class="px-4 py-2">${isEn ? 'Emissions 1%–2% of total' : 'Emissioni 1%–2% del totale'}</td>
            <td class="px-4 py-2">${isEn ? 'Emissions 3%–10% of total' : 'Emissioni 3%–10% del totale'}</td>
            <td class="px-4 py-2">${isEn ? 'Emissions > 10% of total' : 'Emissioni > 10% del totale'}</td>
          </tr>

          <tr class="">
            <td class="px-4 py-2 font-medium">${isEn ? 'Level of Influence (INF)' : 'Livello di Influenza (INF)'}</td>
            <td class="px-4 py-2">${isEn ? 'Minimal ability to act' : 'Capacità minima di agire'}</td>
            <td class="px-4 py-2">${isEn ? 'Limited ability to act' : 'Capacità limitata di agire'}</td>
            <td class="px-4 py-2">${isEn ? 'Good ability to act' : 'Buona capacità di agire'}</td>
            <td class="px-4 py-2">${isEn ? 'Full operational control' : 'Pieno controllo sulle azioni'}</td>
          </tr>

          <tr class="">
            <td class="px-4 py-2 font-medium">Risk / Opportunity (RIS/OPP)</td>
            <td class="px-4 py-2">${isEn ? 'No identifiable risk/opportunity' : 'Nessun rischio/opportunità identificabile'}</td>
            <td class="px-4 py-2">${isEn ? 'Minor risks/opportunities' : 'Rischi/opportunità minori'}</td>
            <td class="px-4 py-2">${isEn ? 'Documented risks/opportunities' : 'Rischi/opportunità evidenti con documentazione'}</td>
            <td class="px-4 py-2">${isEn ? 'Strategic risks/opportunities with direct impact' : 'Rischi/opportunità strategici con impatti diretti'}</td>
          </tr>

          <tr class="">
            <td class="px-4 py-2 font-medium">${isEn ? 'Employee Engagement (IMP)' : 'Impegno dei dipendenti (IMP)'}</td>
            <td class="px-4 py-2">${isEn ? 'No engagement or awareness' : 'Nessun coinvolgimento o consapevolezza'}</td>
            <td class="px-4 py-2">${isEn ? 'Limited engagement or early awareness' : 'Coinvolgimento limitato o consapevolezza iniziale'}</td>
            <td class="px-4 py-2">${isEn ? 'Good engagement and internal documentation' : 'Buon livello di coinvolgimento e documentazione interna'}</td>
            <td class="px-4 py-2">${isEn ? 'High engagement with structured incentives' : 'Alto coinvolgimento e sistemi di incentivi ben strutturati'}</td>
          </tr>

          <tr class="">
            <td class="px-4 py-2 font-medium">${isEn ? 'Data Availability (DD)' : 'Disponibilità Dati (DD)'}</td>
            <td class="px-4 py-2">${isEn ? 'Data unavailable or extremely difficult to obtain' : 'Dati non disponibili o estremamente difficili da ottenere'}</td>
            <td class="px-4 py-2">${isEn ? 'Partially available or imprecise data' : 'Dati parzialmente imprecisi e parzialmente disponibili'}</td>
            <td class="px-4 py-2">${isEn ? 'Mostly available and reliable data' : 'Dati per lo più disponibili e affidabili'}</td>
            <td class="px-4 py-2">${isEn ? 'Complete, reliable, and easily accessible data' : 'Dati completi, affidabili e facilmente accessibili'}</td>
          </tr>

          <tr class="">
            <td class="px-4 py-2 font-medium">${isEn ? 'Reporting Implication' : 'Implicazione Rendicontazione'}</td>
            <td class="px-4 py-2">${isEn ? 'Negligible emissions are excluded as they do not materially impact overall performance.' : 'Le emissioni considerate trascurabili non sono incluse nel rapporto GHG poiché non influiscono significativamente sulle prestazioni complessive.'}</td>
            <td class="px-4 py-2">${isEn ? 'Low-impact emissions are normally excluded due to limited relevance.' : 'Le emissioni a basso impatto non sono incluse poiché non sufficientemente significative.'}</td>
            <td class="px-4 py-2">${isEn ? 'Significant emissions are included with mitigation actions highlighted.' : 'Le emissioni rilevanti sono incluse nel rapporto con evidenza delle azioni di mitigazione.'}</td>
            <td class="px-4 py-2">${isEn ? 'Highly significant emissions receive top reporting and mitigation priority.' : 'Le emissioni con significatività considerevole ricevono massima priorità nel rapporto e nelle strategie di riduzione.'}</td>
          </tr>
        </tbody>
      </table>


      </div>
      `;
  }

  private injectSection6Content() {
    const section6 = this.sections.find((s) => s.id === 6);
    if (!section6) return;

    const generated = this.buildSection6Boundaries();

    // Avoid overwriting user edits
    if (!section6.content || section6.content.trim() === '') {
      section6.content = generated;
      section6.isEditing = false;
      this.saveSectionsToLocal();
    }
  }

  private saveSectionsToLocal() {
    const cleanSections = this.sections.map(
      ({ id, title, content, isEditing }) => ({
        id,
        title,
        content,
        isEditing,
      }),
    );
    // Use localeService.getLanguage() to match what preview component uses
    const currentLang = this.localeService.getLanguage() || 'english';
    const storedKey = `${REPORT_SECTIONS_KEY}_${currentLang}`;
    localStorage.setItem(storedKey, JSON.stringify(cleanSections));
  }

  editSection(section: ReportSection) {
    if (section.id === 7) return;
    (section as any)._backup = section.content;
    section.isEditing = true;
  }

  cancelEdit(section: ReportSection) {
    section.content = (section as any)._backup;
    delete (section as any)._backup;
    section.isEditing = false;
  }

  saveSection(section: ReportSection) {
    section.isEditing = false;
    this.saveSectionsToLocal();

    if (this.companyDetails?.id) {
      this.saveReportToDB();
    }
  }

  async saveReportToDB() {
    if (!this.calculationId) return;

    const reportPayload = {
      language: this.currentLanguage,
      sections: this.sections,
    };

    try {
      await this.api.UpdateCompanyDetails({
        id: this.calculationId,
        report: JSON.stringify(reportPayload),
      });
    } catch (e) {
      console.error('Save report failed', e);
    }
  }

  async loadReportFromDB() {
    try {
      if (!this.calculationId) {
        console.warn('Skipping loadReportFromDB: calculationId missing');
        // Load default sections even without calculationId
        this.sections = buildDefaultSections(
          this.companyDetails,
          this.currentLanguage,
        );
        this.saveSectionsToLocal();
        return;
      }
      const record = await this.api.GetCompanyDetails(this.calculationId);
      this.companyDetailsRecord = record;

      if (record?.report) {
        // Report exists in DB - load it
        const parsed = JSON.parse(record.report);
        this.sections = parsed.sections?.length
          ? parsed.sections
          : buildDefaultSections(this.companyDetails, this.currentLanguage);
        this.currentLanguage = parsed.language || this.currentLanguage;
        this.saveSectionsToLocal();
        this.spinner.hide();
      } else {
        // Report is blank - load default sections and save to DB
        this.sections = buildDefaultSections(
          this.companyDetails,
          this.currentLanguage,
        );
        this.saveSectionsToLocal();
        await this.saveReportToDB();
        this.spinner.hide();
      }
    } catch (err) {
      console.error('Error loading report from DB', err);
      // On error, still load default sections so UI is not empty
      this.sections = buildDefaultSections(
        this.companyDetails,
        this.currentLanguage,
      );
      this.saveSectionsToLocal();
      this.spinner.hide();
    }
  }

  previewPdf(event: Event) {
    this.download(event, true);
  }

  download(event: Event, previewMode: boolean = false) {
    this.spinner.show();
    if (!previewMode) {
      this.saveReportToDB();
    }
    event.preventDefault();
    event.stopPropagation();

    const source = document.getElementById('pdfContent');
    if (!source) {
      console.error('pdfContent not found');
      this.spinner.hide();
      return;
    }

    // Clone content
    const clone = source.cloneNode(true) as HTMLElement;
    clone.id = 'pdfDownload';
    clone.classList.add('pdf-page');

    // Remove UI-only elements from clone
    clone.querySelectorAll('.ui-only').forEach((el) => el.remove());
    clone.querySelectorAll('button').forEach((el) => el.remove());

    // Safety fixes for colors and layout
    this.fixColors(clone);

    // Hidden container - must match html2pdf's internal container width (180mm)
    // so that measurements for page break calculations match the final render
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '180mm';
    container.appendChild(clone);
    document.body.appendChild(container);

    // Split tables at page boundaries AFTER DOM append so measurements work
    this.splitTablesForPageBreaks(clone);

    const options = {
      margin: [15, 15, 25, 15], // top, left, bottom, right in mm
      filename: `${this.companyDetailsRecord.calculationName}_Report_${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      },
      pagebreak: {
        mode: ['css', 'legacy'],
        before: '.page-break-before',
        after: '.page-break-after',
        avoid: ['img', 'figure'],
      },
    };

    // Load footer and header logo images
    const footerLogoImg = new Image();
    footerLogoImg.crossOrigin = 'anonymous';
    footerLogoImg.src = '/images/carbon-bottom.png';

    const headerLogoImg = new Image();
    headerLogoImg.crossOrigin = 'anonymous';
    headerLogoImg.src = '/images/carbon-logo.png';

    let imagesLoaded = 0;
    const totalImages = 2;

    const onImageLoad = () => {
      imagesLoaded++;
      if (imagesLoaded < totalImages) return;

      html2pdf()
        .set(options)
        .from(clone)
        .toPdf()
        .get('pdf')
        .then((pdf: any) => {
          const totalPages = pdf.internal.getNumberOfPages();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const pageWidth = pdf.internal.pageSize.getWidth();

          // Add header logo (top-right) and footer logo (bottom-left) to each page (skip first page)
          for (let i = 2; i <= totalPages; i++) {
            pdf.setPage(i);
            pdf.addImage(headerLogoImg, 'PNG', pageWidth - 30, 5, 20, 12);
            pdf.addImage(footerLogoImg, 'PNG', 15, pageHeight - 20, 20, 12);
          }

          return pdf;
        })
        .then((pdfResult: any) => {
          if (previewMode) {
            // Preview mode: open PDF in new tab
            const pdfBlob = pdfResult.output('blob');
            const blobUrl = URL.createObjectURL(pdfBlob);
            window.open(blobUrl, '_blank');
            document.body.removeChild(container);
            this.spinner.hide();
          } else {
            // Download mode: save the PDF
            pdfResult.save();
            document.body.removeChild(container);

            setTimeout(() => {
              this.currentcat.isPreviewMode = false;
              localStorage.setItem('isPreviewMode', 'false');
              this.spinner.hide();
            });
            this.router.navigate(['/calculation']).then(() => {
              setTimeout(() => window.location.reload(), 300);
            });
          }
        })
        .catch((err: Error) => {
          console.error('PDF generation failed', err);
          document.body.removeChild(container);

          setTimeout(() => {
            this.spinner.hide();
          });
        });
    };

    footerLogoImg.onload = onImageLoad;
    headerLogoImg.onload = onImageLoad;

    footerLogoImg.onerror = () => {
      imagesLoaded++;
      if (imagesLoaded >= totalImages) onImageLoad();
    };

    headerLogoImg.onerror = () => {
      imagesLoaded++;
      if (imagesLoaded >= totalImages) onImageLoad();
    };
  }

  /**
   * Download PDF using PDFShift API - better handling of page breaks
   */
  async downloadWithPdfShift(event: Event, previewMode: boolean = false) {
    this.spinner.show();
    if (!previewMode) {
      this.saveReportToDB();
    }
    event.preventDefault();
    event.stopPropagation();

    const source = document.getElementById('pdfContent');
    if (!source) {
      console.error('pdfContent not found');
      this.spinner.hide();
      return;
    }

    // Clone and prepare content
    const clone = source.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('.ui-only').forEach((el) => el.remove());
    clone.querySelectorAll('button').forEach((el) => el.remove());

    // Get all stylesheets from the page
    const styles = Array.from(document.styleSheets)
      .map((sheet) => {
        try {
          return Array.from(sheet.cssRules)
            .map((rule) => rule.cssText)
            .join('\n');
        } catch (e) {
          return '';
        }
      })
      .join('\n');

    // Build complete HTML document for PDFShift
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          ${styles}

          /* PDF-specific styles for proper page breaks */
          @media print {
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }

          /* Prevent table rows from being cut */
          tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          /* Prevent table cells from being cut */
          td, th {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          /* Keep table headers with body */
          thead {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          /* Prevent images from being cut */
          img {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          /* Prevent paragraphs from being cut */
          p {
            orphans: 3;
            widows: 3;
          }

          /* Keep headings with following content */
          h1, h2, h3, h4, h5, h6 {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }

          /* Page break classes */
          .page-break-before {
            page-break-before: always !important;
          }
          .page-break-after {
            page-break-after: always !important;
          }
        </style>
      </head>
      <body style="margin: 0; padding: 20px; font-family: 'Avenir LT Std', sans-serif;">
        ${clone.outerHTML}
      </body>
      </html>
    `;

    const filename = `${this.companyDetailsRecord.calculationName}_Report_${new Date().toISOString().split('T')[0]}.pdf`;

    try {
      if (previewMode) {
        await this.pdfService.generateAndPreview(htmlContent, {
          margin: { top: '15mm', right: '15mm', bottom: '25mm', left: '15mm' },
          format: 'A4',
          use_print: true,
        });
      } else {
        await this.pdfService.generateAndDownload(htmlContent, filename, {
          margin: { top: '15mm', right: '15mm', bottom: '25mm', left: '15mm' },
          format: 'A4',
          use_print: true,
        });

        this.currentcat.isPreviewMode = false;
        localStorage.setItem('isPreviewMode', 'false');
        this.router.navigate(['/calculation']).then(() => {
          setTimeout(() => window.location.reload(), 300);
        });
      }
    } catch (error) {
      console.error('PDF generation failed:', error);
    } finally {
      this.spinner.hide();
    }
  }

  previewPdfShift(event: Event) {
    this.downloadWithPdfShift(event, true);
  }

  private fixColors(root: HTMLElement) {
    const elements = root.querySelectorAll<HTMLElement>('*');

    elements.forEach((el) => {
      const style = window.getComputedStyle(el);

      // TEXT COLOR
      if (style.color.includes('oklch')) {
        el.style.color = '#111827'; // gray-900
      }

      // BACKGROUND
      if (style.backgroundColor.includes('oklch')) {
        el.style.backgroundColor = '#ffffff';
      }

      // BORDER
      if (style.borderColor.includes('oklch')) {
        el.style.borderColor = '#e5e7eb'; // gray-200
      }

      // OUTLINE (important)
      if ((style as any).outlineColor?.includes('oklch')) {
        el.style.outlineColor = '#e5e7eb';
      }

      // SHADOWS (Tailwind uses OKLCH internally)
      if (style.boxShadow && style.boxShadow.includes('oklch')) {
        el.style.boxShadow = 'none';
      }
    });
  }

  /**
   * Splits tables at page boundaries based on actual DOM measurements.
   * Only splits where a row would cross a page break, keeping the table
   * intact where there is enough space. Repeats the thead on continuation tables.
   * Must be called AFTER the clone is appended to the DOM.
   */
  private splitTablesForPageBreaks(root: HTMLElement) {
    // html2pdf internal page height: A4 (297mm) - margins (15mm top + 25mm bottom) = 257mm
    // toPx(257, 72/25.4) = Math.floor(257 * (72/25.4) / 72 * 96) = 971
    const PAGE_HEIGHT_PX = 971;

    const rootTop = root.getBoundingClientRect().top;

    // --- PASS 1: Split tables at row-level page boundaries ---
    const tables = Array.from(root.querySelectorAll('table'));

    tables.forEach((table) => {
      const thead = table.querySelector('thead');
      const tbody = table.querySelector('tbody');
      if (!thead || !tbody) return;

      const rows = Array.from(
        tbody.querySelectorAll(':scope > tr'),
      ) as HTMLTableRowElement[];
      if (rows.length <= 1) return;

      const theadHeight = thead.getBoundingClientRect().height;

      // Measure each row relative to root
      const rowMeasurements = rows.map((row) => {
        const rect = row.getBoundingClientRect();
        return { top: rect.top - rootTop, bottom: rect.bottom - rootTop };
      });

      // Find split points, accounting for the cumulative shift from inserted headers
      const splitIndices: number[] = [];
      let cumulativeShift = 0;

      for (let i = 0; i < rows.length; i++) {
        const adjTop = rowMeasurements[i].top + cumulativeShift;
        const adjBottom = rowMeasurements[i].bottom + cumulativeShift;

        const startPage = Math.floor(adjTop / PAGE_HEIGHT_PX);
        const endPage = Math.floor((adjBottom - 1) / PAGE_HEIGHT_PX);

        if (endPage > startPage) {
          splitIndices.push(i);
          // Padding fills rest of current page, then new header on next page
          const paddingToNextPage = PAGE_HEIGHT_PX - (adjTop % PAGE_HEIGHT_PX);
          cumulativeShift += paddingToNextPage + theadHeight;
        }
      }

      if (splitIndices.length === 0) return;

      // Build row groups
      const groups: HTMLTableRowElement[][] = [];
      let lastIdx = 0;
      for (const splitIdx of splitIndices) {
        if (splitIdx > lastIdx) {
          groups.push(rows.slice(lastIdx, splitIdx));
        }
        lastIdx = splitIdx;
      }
      if (lastIdx < rows.length) {
        groups.push(rows.slice(lastIdx));
      }
      if (groups.length <= 1) return;

      const tableClasses = table.className;
      const tableStyle = table.getAttribute('style') || '';
      const fragment = document.createDocumentFragment();

      groups.forEach((group, groupIdx) => {
        const miniTable = document.createElement('table');
        miniTable.className = tableClasses;
        if (tableStyle) miniTable.setAttribute('style', tableStyle);

        miniTable.appendChild(thead.cloneNode(true));

        const newTbody = document.createElement('tbody');
        group.forEach((row) => newTbody.appendChild(row.cloneNode(true)));
        miniTable.appendChild(newTbody);

        if (groupIdx > 0) miniTable.style.marginTop = '-1px';
        if (groupIdx < groups.length - 1) miniTable.style.marginBottom = '0';

        fragment.appendChild(miniTable);
      });

      table.parentNode?.replaceChild(fragment, table);
    });

    // --- PASS 2: Protect block elements (p, h1-h6, li, blockquote) from being cut ---
    // Re-read rootTop after table splits may have shifted content
    const rootTop2 = root.getBoundingClientRect().top;
    const blocks = Array.from(
      root.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, blockquote'),
    ) as HTMLElement[];

    blocks.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const elTop = rect.top - rootTop2;
      const elBottom = rect.bottom - rootTop2;
      const elHeight = rect.height;

      // Only protect elements that fit on a single page
      if (elHeight >= PAGE_HEIGHT_PX) return;

      const startPage = Math.floor(elTop / PAGE_HEIGHT_PX);
      const endPage = Math.floor((elBottom - 1) / PAGE_HEIGHT_PX);

      if (endPage > startPage) {
        // Element crosses a page boundary — add padding before it to push to next page
        const remainingOnPage = PAGE_HEIGHT_PX - (elTop % PAGE_HEIGHT_PX);
        const pad = document.createElement('div');
        pad.style.height = remainingOnPage + 'px';
        pad.style.display = 'block';
        el.parentNode?.insertBefore(pad, el);
      }
    });
  }
  significanceRows: SignificanceRow[] = [];

  private loadSignificanceMatrix() {
    const raw = localStorage.getItem('categories');
    if (!raw) return;

    const categories: CategoryLS[] = JSON.parse(raw);

    this.significanceRows = categories
      .flatMap((cat) =>
        (cat.significanceMatrix || []).map((row) => ({
          ...row,
          categoryId: cat.id,
        })),
      )
      .filter((row) => row.ls !== undefined);
  }

  private safeHtmlCache = new Map<string, SafeHtml>();

  getSafeHtml(content: string): SafeHtml {
    if (!content) return '';

    // Use cached version if content hasn't changed
    const cached = this.safeHtmlCache.get(content);
    if (cached) return cached;

    // Unwrap CKEditor's <figure class="table"> wrapper so tables render correctly
    const processed = content.replace(
      /<figure[^>]*class="[^"]*\btable\b[^"]*"[^>]*>([\s\S]*?)<\/figure>/gi,
      '$1',
    );

    const safeHtml = this.sanitizer.bypassSecurityTrustHtml(processed);
    this.safeHtmlCache.set(content, safeHtml);
    return safeHtml;
  }

  goBack(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.currentcat.isPreviewMode = false;
    localStorage.setItem('isPreviewMode', 'false');
    this.router.navigate(['/calculation']).then(() => {
      // setTimeout(() => {
      window.location.reload();
      // }, 300);
    });
  }

  async preview(event: Event) {
    if (!this.validateReportBeforeGenerate()) return;
    event.preventDefault();
    event.stopPropagation();

    this.spinner.show();

    // Save current sections to localStorage first, then convert charts to images and upload to S3
    this.saveSectionsToLocal();

    try {
      await this.convertChartsToImagesAsync();
    } catch (error) {
      console.error('Error uploading chart images to S3:', error);
      this.toastr.error('Failed to upload chart images. Please try again.');
      this.spinner.hide();
      return;
    }

    // Verify localStorage was updated correctly
    const currentLang = this.localeService.getLanguage() || 'english';
    const storedKey = `${REPORT_SECTIONS_KEY}_${currentLang}`;
    const verifyData = JSON.parse(localStorage.getItem(storedKey) || '[]');
    const verifySection7 = verifyData.find((s: any) => s.id === 7);

    this.spinner.hide();

    // Show preview modal
    this.showPreviewModal = true;
    document.body.style.overflow = 'hidden';
  }

  /**
   * Convert canvas to blob for S3 upload
   */
  private canvasToBlob(
    canvas: HTMLCanvasElement,
    scale: number = 2,
    quality: number = 0.85,
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width * scale;
      tempCanvas.height = canvas.height * scale;
      const tempCtx = tempCanvas.getContext('2d');
      if (tempCtx) {
        tempCtx.fillStyle = '#ffffff';
        tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
        tempCtx.scale(scale, scale);
        tempCtx.drawImage(canvas, 0, 0);
      }
      tempCanvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to convert canvas to blob'));
          }
        },
        'image/jpeg',
        quality,
      );
    });
  }

  /**
   * Upload image blob to S3 and return the public URL
   */
  private async uploadImageToS3(
    blob: Blob,
    imageName: string,
    contentType: string = 'image/jpeg',
  ): Promise<string> {
    const extension = contentType.split('/')[1] || 'jpg';
    const fileName = `report-images/${Date.now()}_${imageName}.${extension}`;

    await uploadData({
      path: fileName,
      data: blob,
      options: {
        contentType: contentType,
      },
    }).result;

    // Construct permanent public URL (same pattern as CKEditor uploads)
    return `https://${environment.s3.bucket}.s3.${environment.s3.region}.amazonaws.com/${fileName}`;
  }

  /**
   * Convert base64 data URL to Blob
   */
  private base64ToBlob(dataUrl: string): { blob: Blob; contentType: string } {
    const parts = dataUrl.split(',');
    const mimeMatch = parts[0].match(/:(.*?);/);
    const contentType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const base64Data = parts[1];
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return { blob: new Blob([byteArray], { type: contentType }), contentType };
  }

  /**
   * Process HTML content and replace all base64 images with S3 URLs
   */
  private async replaceBase64ImagesWithS3Urls(
    htmlContent: string,
  ): Promise<string> {
    console.log(
      `[PDF] Original HTML size: ${(htmlContent.length / 1024).toFixed(2)} KB`,
    );

    // Check for any data:image patterns to debug
    const dataImageCount = (htmlContent.match(/data:image/g) || []).length;
    console.log(
      `[PDF] Found ${dataImageCount} occurrences of "data:image" in HTML`,
    );

    // Find all base64 image sources in the HTML (both double and single quotes)
    const base64RegexDouble = /src="(data:image\/[^;]+;base64,[^"]+)"/g;
    const base64RegexSingle = /src='(data:image\/[^;]+;base64,[^']+)'/g;

    const matchesDouble = [...htmlContent.matchAll(base64RegexDouble)];
    const matchesSingle = [...htmlContent.matchAll(base64RegexSingle)];
    const allMatches = [...matchesDouble, ...matchesSingle];

    console.log(
      `[PDF] Regex matches - double quotes: ${matchesDouble.length}, single quotes: ${matchesSingle.length}`,
    );

    // If no matches but data:image exists, the format might be different
    if (allMatches.length === 0 && dataImageCount > 0) {
      console.log(
        '[PDF] WARNING: data:image found but regex did not match. Checking HTML sample...',
      );
      const dataImageIndex = htmlContent.indexOf('data:image');
      if (dataImageIndex > -1) {
        console.log(
          '[PDF] Sample around data:image:',
          htmlContent.substring(
            Math.max(0, dataImageIndex - 50),
            dataImageIndex + 100,
          ),
        );
      }
    }

    if (allMatches.length === 0) {
      console.log('[PDF] No base64 images found in HTML');
      return htmlContent;
    }

    console.log(
      `[PDF] Found ${allMatches.length} base64 images to upload to S3`,
    );

    let updatedContent = htmlContent;
    let successCount = 0;

    // Upload each base64 image to S3 and replace with URL
    for (let i = 0; i < allMatches.length; i++) {
      const match = allMatches[i];
      const base64DataUrl = match[1];
      const base64Size = (base64DataUrl.length / 1024).toFixed(2);

      console.log(
        `[PDF] Processing image ${i + 1}/${allMatches.length} (${base64Size} KB)`,
      );

      try {
        const { blob, contentType } = this.base64ToBlob(base64DataUrl);
        const imageUrl = await this.uploadImageToS3(
          blob,
          `image_${i}_${Date.now()}`,
          contentType,
        );

        // Replace this specific base64 URL with S3 URL
        updatedContent = updatedContent.replace(base64DataUrl, imageUrl);
        successCount++;
        console.log(
          `[PDF] Uploaded image ${i + 1}/${allMatches.length} to S3: ${imageUrl}`,
        );
      } catch (error) {
        console.error(`[PDF] Error uploading base64 image ${i} to S3:`, error);
        // Keep the base64 data if upload fails
      }
    }

    console.log(
      `[PDF] Successfully uploaded ${successCount}/${allMatches.length} images`,
    );
    console.log(
      `[PDF] Final HTML size: ${(updatedContent.length / 1024).toFixed(2)} KB`,
    );

    return updatedContent;
  }

  /**
   * Convert charts to images and upload to S3, replacing canvas tags with img tags using S3 URLs
   */
  private async convertChartsToImagesAsync(): Promise<void> {
    const categories = JSON.parse(localStorage.getItem('categories') || '[]');
    const currentLang = this.localeService.getLanguage() || 'english';
    const storedKey = `${REPORT_SECTIONS_KEY}_${currentLang}`;

    const storedSections = JSON.parse(localStorage.getItem(storedKey) || '[]');
    const section7 = storedSections.find((s: any) => s.id === 7);
    if (!section7) return;

    let updatedContent = section7.content;

    // Upload category charts to S3
    for (const category of categories) {
      const canvasId = `chart_${category.id}`;
      const canvas = document.getElementById(
        canvasId,
      ) as HTMLCanvasElement | null;

      if (!canvas) {
        const chartInstance = Chart.getChart(canvasId);
        if (chartInstance) {
          try {
            // Get canvas from Chart.js instance
            const chartCanvas = chartInstance.canvas;
            const blob = await this.canvasToBlob(chartCanvas);
            const imageUrl = await this.uploadImageToS3(
              blob,
              `category_${category.id}`,
            );
            updatedContent = updatedContent?.replace(
              new RegExp(
                `<canvas[^>]*id="${canvasId}"[^>]*>[^<]*</canvas>`,
                'gi',
              ),
              `<img src="${imageUrl}" style="width:100%; height:auto; display:block;" alt="Chart ${category.id}"/>`,
            );
          } catch (error) {
            console.error(`Error uploading chart ${canvasId} to S3:`, error);
          }
        } else {
          console.warn(
            `Canvas and Chart.js instance not found for ${canvasId}`,
          );
        }
        continue;
      }

      try {
        const blob = await this.canvasToBlob(canvas);
        const imageUrl = await this.uploadImageToS3(
          blob,
          `category_${category.id}`,
        );

        updatedContent = updatedContent?.replace(
          new RegExp(`<canvas[^>]*id="${canvasId}"[^>]*>[^<]*</canvas>`, 'gi'),
          `<img src="${imageUrl}" style="width:100%; height:auto; display:block;" alt="Chart ${category.id}"/>`,
        );
      } catch (error) {
        console.error(`Error uploading chart ${canvasId} to S3:`, error);
      }
    }

    // Upload pie chart to S3
    const pieCanvas = document.getElementById(
      'emissionsPieChart',
    ) as HTMLCanvasElement | null;
    if (pieCanvas) {
      try {
        const blob = await this.canvasToBlob(pieCanvas);
        const imageUrl = await this.uploadImageToS3(
          blob,
          'emissions_pie_chart',
        );

        updatedContent = updatedContent?.replace(
          /<canvas[^>]*id="emissionsPieChart"[^>]*>[^<]*<\/canvas>/gi,
          `<img src="${imageUrl}" style="max-width:100%; height:auto; display:block; margin:0 auto;" alt="Total Emissions Pie Chart"/>`,
        );
      } catch (error) {
        console.error('Error uploading pie chart to S3:', error);
      }
    } else {
      const pieChartInstance = Chart.getChart('emissionsPieChart');
      if (pieChartInstance) {
        try {
          const chartCanvas = pieChartInstance.canvas;
          const blob = await this.canvasToBlob(chartCanvas);
          const imageUrl = await this.uploadImageToS3(
            blob,
            'emissions_pie_chart',
          );

          updatedContent = updatedContent?.replace(
            /<canvas[^>]*id="emissionsPieChart"[^>]*>[^<]*<\/canvas>/gi,
            `<img src="${imageUrl}" style="max-width:100%; height:auto; display:block; margin:0 auto;" alt="Total Emissions Pie Chart"/>`,
          );
        } catch (error) {
          console.error('Error uploading pie chart to S3:', error);
        }
      } else {
        console.warn('Pie chart canvas and Chart.js instance not found');
      }
    }

    // Update section 7 content
    section7.content = updatedContent;
    localStorage.setItem(storedKey, JSON.stringify(storedSections));
  }

  previewFromPreview() {
    this.downloadFromPreviewWithPdfShift(true);
  }

  /**
   * Download from preview modal using PDFShift API
   */
  async downloadFromPreviewWithPdfShift(previewMode: boolean = false) {
    this.spinner.show();
    if (!previewMode) {
      this.saveReportToDB();
    }

    const source = document.getElementById('previewModalContent');
    if (!source) {
      console.error('previewModalContent not found');
      this.spinner.hide();
      return;
    }

    // Clone and prepare content
    const clone = source.cloneNode(true) as HTMLElement;
    clone
      .querySelectorAll('img[src*="carbon-bottom"]')
      .forEach((el) => el.remove());

    // Get all stylesheets from the page
    const styles = Array.from(document.styleSheets)
      .map((sheet) => {
        try {
          return Array.from(sheet.cssRules)
            .map((rule) => rule.cssText)
            .join('\n');
        } catch (e) {
          return '';
        }
      })
      .join('\n');

    // Build complete HTML document for PDFShift
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          ${styles}

          @media print {
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }

          /* Prevent table rows from being cut */
          tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          td, th {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          thead {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          img {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          p {
            orphans: 3;
            widows: 3;
          }

          h1, h2, h3, h4, h5, h6 {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }

          .page-break-before {
            page-break-before: always !important;
          }
          .page-break-after {
            page-break-after: always !important;
          }

          /* Prevent content leaking from page 1 when header/footer start at page 2 */
          @page :first {
            margin-top: 0;
            margin-bottom: 0;
          }
        </style>
      </head>
      <body style="margin: 0; padding: 20px; font-family: 'Avenir LT Std', sans-serif; background: white;">
        ${clone.outerHTML}
      </body>
      </html>
    `;

    // Replace all base64 images with S3 URLs to reduce payload size
    const processedHtmlContent =
      await this.replaceBase64ImagesWithS3Urls(htmlContent);

    const filename = `${this.companyDetailsRecord.calculationName}_Report_${new Date().toISOString().split('T')[0]}.pdf`;

    // Header image (top-right corner) - appears on all pages except the first
    const headerHtml = `<div style="width: 100%; text-align: right; padding: 0; margin: 0;">
      <img src="${environment.imagesBaseUrl}/carbon-second-image-new.png" style="width: 67px; height: 57px;" />
    </div>`;

    // Footer image (bottom-left corner) - appears on all pages except the first
    const footerHtml = `<div style="width: 100%; text-align: left; padding: 0; margin: 0;">
      <img src="${environment.imagesBaseUrl}/carbon-bottom-new.png" style="width: 80px;" />
    </div>`;

    const pdfOptions = {
      margin: { top: '10mm', right: '15mm', bottom: '15mm', left: '15mm' },
      format: 'A4' as const,
      use_print: true,
      sandbox: false, // Allow PDFShift to fetch external resources (S3 images)
      header: { source: headerHtml, height: '30px', start_at: 2 },
      footer: { source: footerHtml, height: '30px', start_at: 2 },
    };

    try {
      if (previewMode) {
        await this.pdfService.generateAndPreview(
          processedHtmlContent,
          pdfOptions,
        );
      } else {
        await this.pdfService.generateAndDownload(
          processedHtmlContent,
          filename,
          pdfOptions,
        );
        this.closePreviewModal();
      }
    } catch (error: any) {
      console.error('PDF generation failed:', error);
      // Extract error message from PDFShift API response
      let errorMessage = 'PDF generation failed. Please try again.';
      if (error?.error instanceof Blob) {
        // PDFShift returns error as JSON in blob format
        try {
          const errorText = await error.error.text();
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorMessage;
        } catch (e) {
          // If parsing fails, use default message
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      this.toastr.error(errorMessage, 'PDF Error');
    } finally {
      this.spinner.hide();
    }
  }

  downloadFromPreview(previewMode: boolean = false) {
    this.spinner.show();
    if (!previewMode) {
      this.saveReportToDB();
    }
    const source = document.getElementById('previewModalContent');
    if (!source) {
      console.error('previewModalContent not found');
      this.spinner.hide();
      return;
    }

    const clone = source.cloneNode(true) as HTMLElement;
    clone.id = 'pdfDownloadPreview';

    // Remove footer logos from clone (will be added via jsPDF to each page)
    clone
      .querySelectorAll('img[src*="carbon-bottom"]')
      .forEach((el) => el.remove());

    // Set white background on clone and all child elements for PDF
    clone.style.backgroundColor = '#ffffff';
    clone.querySelectorAll('*').forEach((el: Element) => {
      const htmlEl = el as HTMLElement;
      if (htmlEl.style) {
        const computedBg = window.getComputedStyle(htmlEl).backgroundColor;
        // Replace gray backgrounds with white
        if (
          computedBg === 'rgb(232, 232, 232)' ||
          computedBg === '#E8E8E8' ||
          computedBg === '#e8e8e8'
        ) {
          htmlEl.style.backgroundColor = '#ffffff';
        }
      }
    });

    // Hidden container - must match html2pdf's internal container width (180mm)
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '180mm';
    container.style.backgroundColor = '#ffffff';
    container.appendChild(clone);
    document.body.appendChild(container);

    // Fix unsupported oklch colors that html2canvas can't parse
    this.fixColors(clone);
    this.splitTablesForPageBreaks(clone);

    const options = {
      margin: [15, 15, 25, 15], // top, left, bottom, right in mm
      filename: `${this.companyDetailsRecord.calculationName}_Report_${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      },
      pagebreak: {
        mode: ['css', 'legacy'],
        before: '.page-break-before',
        after: '.page-break-after',
        avoid: ['img', 'figure'],
      },
    };

    // Load footer and header logo images
    const footerLogoImg = new Image();
    footerLogoImg.crossOrigin = 'anonymous';
    footerLogoImg.src = '/images/carbon-bottom-new.png';

    const headerLogoImg = new Image();
    headerLogoImg.crossOrigin = 'anonymous';
    headerLogoImg.src = '/images/carbon-second-image-new.png';

    // Wait for both images to load
    let imagesLoaded = 0;
    const totalImages = 2;

    const onImageLoad = () => {
      imagesLoaded++;
      if (imagesLoaded < totalImages) return;

      html2pdf()
        .set(options)
        .from(clone)
        .toPdf()
        .get('pdf')
        .then((pdf: any) => {
          const totalPages = pdf.internal.getNumberOfPages();
          const pageHeight = pdf.internal.pageSize.getHeight(); // 297mm for A4
          const pageWidth = pdf.internal.pageSize.getWidth(); // 210mm for A4

          // Add header logo (top-right) and footer logo (bottom-left) to each page (skip first page)
          for (let i = 2; i <= totalPages; i++) {
            pdf.setPage(i);
            // Header logo - top right corner
            pdf.addImage(headerLogoImg, 'PNG', pageWidth - 30, 5, 20, 12);
            // Footer logo - bottom left corner
            pdf.addImage(footerLogoImg, 'PNG', 15, pageHeight - 20, 20, 12);
          }

          return pdf;
        })
        .then((pdfResult: any) => {
          if (previewMode) {
            // Preview mode: open PDF in new tab
            const pdfBlob = pdfResult.output('blob');
            const blobUrl = URL.createObjectURL(pdfBlob);
            window.open(blobUrl, '_blank');
          } else {
            // Download mode: save the PDF
            pdfResult.save();
            setTimeout(() => {
              this.closePreviewModal();
            });
          }
        })
        .catch((error: Error) => {
          console.error('PDF generation failed:', error);
        })
        .finally(() => {
          document.body.removeChild(container);
          setTimeout(() => {
            this.spinner.hide();
          });
        });
    };

    footerLogoImg.onload = onImageLoad;
    headerLogoImg.onload = onImageLoad;

    footerLogoImg.onerror = () => {
      imagesLoaded++;
      if (imagesLoaded >= totalImages) onImageLoad();
    };

    headerLogoImg.onerror = () => {
      imagesLoaded++;
      if (imagesLoaded >= totalImages) onImageLoad();
    };
  }

  closePreviewModal() {
    this.showPreviewModal = false;
    this.previewContent = '';
    document.body.style.overflow = 'auto';
  }

  getCategoryName(categoryId: string): string {
    const isEn = this.currentLanguage === 'english';
    const mapEn: Record<string, string> = {
      '0': 'Category 1',
      '1': 'Category 2',
      '2': 'Category 3',
      '3': 'Category 4',
      '4': 'Category 5',
      '5': 'Category 6',
    };
    const mapIt: Record<string, string> = {
      '0': 'Categoria 1',
      '1': 'Categoria 2',
      '2': 'Categoria 3',
      '3': 'Categoria 4',
      '4': 'Categoria 5',
      '5': 'Categoria 6',
    };
    const map = isEn ? mapEn : mapIt;
    return (
      map[categoryId] ??
      (isEn ? `Category ${categoryId}` : `Categoria ${categoryId}`)
    );
  }

  formatCategoryId(categoryId: string): string {
    const isEn = this.currentLanguage === 'english';
    // Extract number from "category1", "category2", etc.
    const num = categoryId.replace(/\D/g, '');
    return isEn ? `Category ${num}` : `Categoria ${num}`;
  }

  private injectSection8Content() {
    const section8 = this.sections.find((s) => s.id === 8);
    if (!section8) return;

    const generated = this.buildCommentaryContent();

    // Avoid overwriting user edits
    if (!section8.content || section8.content.trim() === '') {
      section8.content = generated;
      section8.isEditing = false;
      this.saveSectionsToLocal();
    }
  }

  private calculateGrandTotals(
    data: Record<string, any>,
    includedCategoryIds?: string[],
  ) {
    let totals = {
      co2: 0,
      ch4: 0,
      n2o: 0,
      co2e: 0,
    };

    Object.entries(data || {}).forEach(([categoryId, row]: [string, any]) => {
      if (includedCategoryIds && !includedCategoryIds.includes(categoryId)) {
        return;
      }

      if (!row || typeof row !== 'object') return;

      totals.co2 += Number(row.co2 || 0);
      totals.ch4 += Number(row.ch4 || 0);
      totals.n2o += Number(row.n2o || 0);
      totals.co2e += Number(row.co2e || 0);
    });

    return totals;
  }

  buildCommentaryContent(): string {
    const isEn = this.currentLanguage === 'english';
    const categories: any[] = JSON.parse(
      localStorage.getItem('categories') || '[]',
    );

    // ---- Build categoryTotals keyed by categoryId ----
    const categoryTotals: Record<string, CategoryTotals> = {};
    categories.forEach((cat: any) => {
      if (cat.id && cat.calculation_category_totals) {
        categoryTotals[cat.id] = cat.calculation_category_totals;
      }
    });

    // ---- Fallback if no data ----
    if (!Object.keys(categoryTotals).length) {
      return this.getFallbackChapter8Text();
    }

    const companyName = this.companyDetails.companyName;

    // ---- Calculate LS per category (same logic as Chapter 7) ----
    const categoryLSMap: Record<string, number | null> = {};
    categories.forEach((cat: any) => {
      const catRows = this.significanceRows.filter(
        (r) => r.categoryId === cat.id,
      );
      const rowAverages: number[] = [];

      catRows.forEach((r) => {
        const scores = [
          r.criteria.magnitude,
          r.criteria.influence,
          r.criteria.riskOpportunity,
          r.criteria.stakeholderEngagement,
          r.criteria.dataAccessibility,
        ].map((v) => (v != null && v !== '-' ? Number(v) : null));

        const validScores = scores.filter((v) => v != null) as number[];
        if (validScores.length === 5) {
          rowAverages.push(validScores.reduce((a, b) => a + b, 0) / 5);
        }
      });

      if (rowAverages.length) {
        categoryLSMap[cat.id] =
          rowAverages.reduce((a, b) => a + b, 0) / rowAverages.length;
      } else {
        categoryLSMap[cat.id] = null;
      }
    });

    // ---- Determine significant categories (LS >= 3) ----
    const significantCategoryIds: string[] = categories
      .filter((c: any) => {
        const ls = categoryLSMap[c.id];
        return typeof ls === 'number' && ls >= 3;
      })
      .map((c: any) => c.id);

    // STEP 2: Calculate inventory totals (kg) - ONLY for significant categories
    const grandTotals = this.calculateGrandTotals(
      categoryTotals,
      significantCategoryIds,
    );
    const totalCO2_kg = grandTotals.co2;
    const totalCH4_kg = grandTotals.ch4;
    const totalN2O_kg = grandTotals.n2o;
    const totalCO2e_kg = grandTotals.co2e;

    // ---- Validate totals ----
    if (!totalCO2e_kg || totalCO2e_kg === 0 || isNaN(totalCO2e_kg)) {
      return this.getFallbackChapter8Text();
    }

    // STEP 3: Calculate overall uncertainty (NEW LOGIC)
    const overallUncertainty = this.getInventoryOverallUncertaintyPct(
      significantCategoryIds,
    );
    const uncertaintyText = isNaN(overallUncertainty)
      ? 'N/A'
      : overallUncertainty.toFixed(1);

    // STEP 4: Convert totals to tonnes
    const totalCO2_t = totalCO2_kg / 1000;
    const totalCH4_t = totalCH4_kg / 1000;
    const totalN2O_t = totalN2O_kg / 1000;
    const totalCO2e_t = totalCO2e_kg / 1000;

    // STEP 5: Sort ONLY significant categories by emissions
    const sorted = Object.entries(categoryTotals)
      .filter(
        ([id, v]) => significantCategoryIds.includes(id) && Number(v.co2e) > 0,
      )
      .sort((a, b) => b[1].co2e - a[1].co2e);
    if (!sorted.length) {
      return this.getFallbackChapter8Text();
    }

    const highest = sorted[0];
    const lowest = sorted[sorted.length - 1];
    const highestCategoryName = this.formatCategoryId(highest[0]);
    const lowestCategoryName = this.formatCategoryId(lowest[0]);

    const highestPercentage = ((highest[1].co2e / totalCO2e_kg) * 100).toFixed(
      1,
    );
    const lowestPercentage = ((lowest[1].co2e / totalCO2e_kg) * 100).toFixed(1);

    const primarySources =
      sorted
        .slice(0, 3)
        .map(([id]) => this.formatCategoryId(id))
        .join(', ') || '[PrimarySources]';
    const highImpactAreas =
      sorted
        .slice(0, 2)
        .map(([id]) => this.formatCategoryId(id))
        .join(', ') || '[HighImpactAreas]';

    if (isEn) {
      return `
        <div class="">
          <p class="text-sm">
            The carbon inventory reveals that <span class="highlight">${primarySources}</span>
            are the largest contributors to <span class="highlight">${companyName}</span>'s carbon footprint.
            <span class="highlight">${highestCategoryName}</span> accounts for approximately
            <span class="highlight">${highestPercentage}</span>% of total emissions, primarily due to
            <span class="highlight">[ReasonForHighEmissions]</span>.
            By contrast, <span class="highlight">${lowestCategoryName}</span> contributes only
            <span class="highlight">${lowestPercentage}</span>%, reflecting
            <span class="highlight">[ReasonForLowEmissions]</span>.
          </p>

          <p class="text-sm">
            Total emissions for the reporting period amount to
            <span class="highlight">${totalCO2e_t.toFixed(2)}</span> t CO₂e, with CO₂ representing
            <span class="highlight">${totalCO2_t.toFixed(2)}</span> t,
            CH₄ <span class="highlight">${totalCH4_t.toFixed(4)}</span> t and
            N₂O <span class="highlight">${totalN2O_t.toFixed(4)}</span> t.
            The overall uncertainty for the inventory is
            <span class="highlight">${uncertaintyText}</span>%.
          </p>

          <p class="text-sm">
            These results suggest that the organisation should focus its reduction efforts on
            <span class="highlight">${highImpactAreas}</span>.
            Potential measures include <span class="highlight">[ExampleReduction]</span>.
            Improving data quality and expanding data coverage will further enhance the accuracy
            of future inventories.
          </p>
        </div>
      `;
    }

    return `
      <div class="">
        <p class="text-sm">
          L'inventario del carbonio rivela che <span class="highlight">${primarySources}</span>
          sono i principali contributori all'impronta di carbonio di
          <span class="highlight">${companyName}</span>.
          <span class="highlight">${highestCategoryName}</span> rappresenta circa il
          <span class="highlight">${highestPercentage}</span>% delle emissioni totali, principalmente
          a causa di <span class="highlight">[MotivoDelleAlteEmissioni]</span>.
          Al contrario, <span class="highlight">${lowestCategoryName}</span> contribuisce
          solo per il <span class="highlight">${lowestPercentage}</span>%,
          riflettendo <span class="highlight">[MotivoDelleBasseEmissioni]</span>.
        </p>

        <p class="text-sm">
          Le emissioni totali per il periodo di rendicontazione ammontano a
          <span class="highlight">${totalCO2e_t.toFixed(2)}</span> t CO₂e, con CO₂ che rappresenta
          <span class="highlight">${totalCO2_t.toFixed(2)}</span> t,
          CH₄ <span class="highlight">${totalCH4_t.toFixed(4)}</span> t e
          N₂O <span class="highlight">${totalN2O_t.toFixed(4)}</span> t.
          L'incertezza complessiva per l'inventario è del
          <span class="highlight">${uncertaintyText}</span>%.
        </p>

        <p class="text-sm">
          Questi risultati suggeriscono che l'organizzazione dovrebbe concentrare i propri sforzi
          di riduzione su <span class="highlight">${highImpactAreas}</span>.
          Le potenziali misure includono <span class="highlight">[EsempioDiRiduzione]</span>.
          Migliorare la qualità dei dati e ampliare la copertura dei dati migliorerà ulteriormente
          l'accuratezza degli inventari futuri.
        </p>
      </div>
    `;
  }

  getFallbackChapter8Text(): string {
    const isEn = this.currentLanguage === 'english';
    if (isEn) {
      return `
      <div class="">
        <p class="text-sm">
          The carbon inventory reveals that [PrimarySources] are the largest contributors
          to <strong>[CompanyName]</strong>'s carbon footprint. Category <strong>[CategoryWithHighestEmissions] </strong>
          accounts for approximately <strong>[HighestCategoryPercentage]%</strong> of total emissions,
          primarily due to <strong>[ReasonForHighEmissions]</strong>. By contrast, Category
          <strong>[CategoryWithLowestEmissions]</strong> contributes only <strong>[LowestCategoryPercentage]%</strong>,
          reflecting <strong>[ReasonForLowEmissions]</strong>.
        </p>

        <p class="text-sm">
          Total emissions for the reporting period amount to <strong>[TotalCO2e]</strong> t CO₂e,
          with CO₂ representing <strong>[TotalCO2]</strong> t, CH₄ <strong>[TotalCH4]</strong> t and
          N₂O <strong>[TotalN2O]</strong> t. The overall uncertainty for the inventory is
          <strong>[OverallUncertainty]%</strong>.
        </p>

        <p class="text-sm">
          These results suggest that the organisation should focus its reduction
          efforts on <strong>[HighImpactAreas]</strong>. Potential measures include
          <strong>[ExampleReduction]</strong>. Improving data quality and expanding data coverage
          will further enhance the accuracy of future inventories.
        </p>
      </div>
    `;
    } else {
      return `
      <div class="">
        <p class="text-sm">
          L'inventario del carbonio rivela che [PrimarySources] sono i principali contributori
          all'impronta di carbonio di <strong>[CompanyName]</strong>. La Categoria <strong>[CategoryWithHighestEmissions] </strong>
          rappresenta circa il <strong>[HighestCategoryPercentage]%</strong> delle emissioni totali,
          principalmente a causa di <strong>[MotivoDelleAlteEmissioni]</strong>. Al contrario, la Categoria
          <strong>[CategoryWithLowestEmissions]</strong> contribuisce solo per il <strong>[LowestCategoryPercentage]%</strong>,
          riflettendo <strong>[MotivoDelleBasseEmissioni]</strong>.
        </p>

        <p class="text-sm">
          Le emissioni totali per il periodo di rendicontazione ammontano a <strong>[TotalCO2e]</strong> t CO₂e,
          con CO₂ che rappresenta <strong>[TotalCO2]</strong> t, CH₄ <strong>[TotalCH4]</strong> t e
          N₂O <strong>[TotalN2O]</strong> t. L'incertezza complessiva per l'inventario è del
          <strong>[OverallUncertainty]%</strong>.
        </p>

        <p class="text-sm">
          Questi risultati suggeriscono che l'organizzazione dovrebbe concentrare i propri sforzi di riduzione
          su <strong>[HighImpactAreas]</strong>. Le potenziali misure includono
          <strong>[EsempioDiRiduzione]</strong>. Migliorare la qualità dei dati e ampliare la copertura dei dati
          migliorerà ulteriormente l'accuratezza degli inventari futuri.
        </p>
      </div>
    `;
    }
  }

  private getSectionsWithPlaceholders(): number[] {
    const invalidSections: number[] = [];

    this.sections.forEach((section) => {
      // Skip section 7 & 10
      if ([7, 10].includes(section.id)) return;

      if (!section.content) return;

      this.placeholderRegex.lastIndex = 0;

      if (this.placeholderRegex.test(section.content)) {
        invalidSections.push(section.id);
      }
    });

    return invalidSections;
  }

  private getEmptySections(): number[] {
    return (
      this.sections
        // Skip section 7 & 10
        .filter((section) => ![7, 10].includes(section.id))
        .filter((section) => !section.content || section.content.trim() === '')
        .map((section) => section.id)
    );
  }

  validateReportBeforeGenerate(): boolean {
    const placeholderSections = this.getSectionsWithPlaceholders();
    const emptySections = this.getEmptySections();

    const errors: string[] = [];

    if (placeholderSections.length) {
      const isEn = this.currentLanguage === 'english';
      errors.push(
        isEn
          ? `Sections with missing fields: ${placeholderSections.join(', ')}`
          : `Sezioni con campi mancanti: ${placeholderSections.join(', ')}`,
      );
    }

    if (emptySections.length) {
      const isEn = this.currentLanguage === 'english';
      errors.push(
        isEn
          ? `Sections that are empty or incomplete: ${emptySections.join(', ')}`
          : `Sezioni vuote o incomplete: ${emptySections.join(', ')}`,
      );
    }

    if (errors.length > 0) {
      this.validationErrors = errors;
      this.showValidationModal = true;
      return false;
    }

    return true;
  }
}

function getCountryName(
  code: string | undefined,
  fallback: string,
  language?: string,
): string {
  if (!code) return fallback;

  const lang = language === 'english' ? 'en' : 'it';
  const upperCode = code.toUpperCase();
  return (
    new Intl.DisplayNames([lang], { type: 'region' }).of(upperCode) || fallback
  );
}

function parseCompanyAddress(raw: string | undefined): {
  city: string;
  formattedAddress: string;
} {
  if (!raw) return { city: '', formattedAddress: '' };
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      const city = parsed.city || '';
      const parts = [
        parsed.street,
        [parsed.postalCode, parsed.city].filter(Boolean).join(' '),
        parsed.province ? `(${parsed.province})` : '',
      ].filter(Boolean);
      return { city, formattedAddress: parts.length ? parts.join(', ') : '' };
    }
  } catch {}
  // Legacy plain-string address
  return { city: raw, formattedAddress: raw };
}

function buildDefaultSections(
  company: any,
  language: string = 'english',
): ReportSection[] {
  const name =
    company?.companyName ||
    (language === 'italian' ? "l'Azienda" : 'the Company');
  const sector =
    company?.businessSector ||
    '<span class="highlight">[Business sector]</span>';
  const country = getCountryName(
    company?.country,
    '<span class="highlight">[Country / Region]</span>',
    language,
  );
  const { city: parsedCity, formattedAddress: parsedAddr } =
    parseCompanyAddress(company?.address);
  const city = parsedCity || '<span class="highlight">[City]</span>';
  const address =
    parsedAddr || '<span class="highlight">[Headquarters address]</span>';
  const foundationYear =
    company?.foundationYear || '<span class="highlight">[Year]</span>';
  const productsOrServices =
    company?.productsOrServices ||
    '<span class="highlight">[Products / Services description]</span>';
  const mission =
    company?.mission ||
    '<span class="highlight">to be a point of reference in its sector, offering high-quality products and services</span>';
  const vision =
    company?.vision ||
    '<span class="highlight">to be a cutting-edge company through continuous innovation and adoption of latest technologies</span>';
  const reportingYear =
    company?.reportingYear || '<span class="highlight">[Reporting Year]</span>';
  const reportingPeriodStart = '01/01/' + (reportingYear || '2023');
  const reportingPeriodEnd = '31/12/' + (reportingYear || '2023');
  const yearsOfExperience =
    company?.reportingYear &&
    company?.foundationYear &&
    !isNaN(parseInt(company.reportingYear)) &&
    !isNaN(parseInt(company.foundationYear))
      ? parseInt(company.reportingYear) - parseInt(company.foundationYear)
      : '<span class="highlight">[Years of experience]</span>';
  const overallUncertainty =
    '<span class="highlight">[OverallUncertainty]</span>';

  if (language === 'italian') {
    return buildItalianSections(company, language);
  }

  return [
    {
      id: 1,
      title: '1. Methodological approach',
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm">This approach is based on key principles of relevance, completeness, consistency, transparency and accuracy, ensuring that the emissions balance sheet reflects actual greenhouse gas emissions and the organisation's activities to monitor and reduce them.</p>
            <p class="text-sm">First, the organisational boundaries of <strong>${name}</strong> were identified, determining the activities, sites and operational units to be included in the emissions inventory. Emission sources were then mapped and divided into direct emissions (Category 1) and indirect emissions (Categories 2-6). Direct emissions concern internal activities under the company's control, while indirect emissions include electricity consumption, transport of goods, employee commuting and the production or disposal of products used by the organisation.</p>
            <p class="text-sm">Data collection involved consulting internal documents, energy invoices, technical data sheets and other relevant records. Specific emission factors were applied to each fuel or activity, and country- or region-specific factors were used whenever possible to improve accuracy. All data and methodologies were documented in detail to ensure transparency and consistency. Measurement uncertainties were assessed and recorded, and reduction actions were identified where necessary.</p>
            <p class="text-sm">This report also includes a section on the emission-reduction strategies implemented by <strong>${name}</strong>, highlighting the organisation's efforts to reduce its environmental impact.</p>
          </div>
        `,
    },
    {
      id: 2,
      title: '2. Introduction *',
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm">In the current global scenario, characterised by an unprecedented increase in average Earth temperatures, attention on climate change and the environmental impact of human activities is constantly growing. Climate change, mainly caused by the emission of greenhouse gases (GHG) into the atmosphere, represents one of the most significant environmental, social and economic challenges of our time. These gases—including carbon dioxide (CO₂), methane (CH₄) and nitrous oxide (N₂O)—contribute to the greenhouse effect, a natural process which, when amplified by human activities, can lead to global warming with potentially disastrous consequences.</p>
            <p class="text-sm">The first step to addressing this challenge is measuring and reporting GHG emissions, i.e. determining an organisation's carbon footprint. The carbon footprint represents the total amount of greenhouse gases emitted directly or indirectly through an organisation's activities, expressed in terms of CO₂ equivalents. Quantifying these emissions is fundamental to managing and reducing them, since only what can be measured can be improved.</p>
            <p class="text-sm"><strong>${name}</strong> recognises its role in this global effort and is committed to transparently reporting its greenhouse gas emissions. By publishing this report, the organisation aims to identify its main sources of emissions, evaluate reduction opportunities and engage stakeholders in its sustainability journey.</p>
          </div>
        `,
    },
    {
      id: 3,
      title: '3. Company profile *',
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm"><strong>${name}</strong>, founded in <strong>${foundationYear}</strong> in </strong>${city}</strong>, <strong>${country}</strong>, operates in the <strong>${sector}</strong> sector. Specialising in <strong>${productsOrServices}</strong>, the company stands out for its core business focused on creativity and the offering of high-quality products.</p>
            <p class="text-sm">At the same time, <strong>${name}</strong> is committed to promoting the production of goods that meet high quality standards and comply with current regulations on protection and work ethics. A distinctive element of its approach is the use of <span class="highlight">certified or responsibly sourced materials</span>, often including ecological materials such as <span class="highlight">recycled materials</span>. The company believes it is essential to address social and environmental issues in order to meet commitments made with customers; this approach not only maintains high product quality but also reflects a broader commitment to sustainability.</p>

            <h3 class="text-base font-bold">3.1. Mission</h3>
            <p class="text-sm">The mission of <strong>${name}</strong> is to be a point of reference in its sector, offering high-quality <span class="highlight">products</span> and services for <span class="highlight">its target markets</span>. With over <strong>${yearsOfExperience}</strong> of experience in <strong>${sector}</strong>, the company is committed to providing creative and innovative solutions to its customers—from initial design to final production. Its mission is to meet customer needs by offering products that meet the highest standards of quality, sustainability and compliance with current regulations.</p>

            <h3 class="text-base font-bold">3.2. Vision</h3>
            <p class="text-sm">The vision of <strong>${name}</strong> is to be a cutting-edge company in <strong>${sector}</strong> through continuous innovation and the adoption of the latest technologies. The organisation seeks to expand its international market presence, consolidating its reputation as a reliable and trusted partner. It aims to become a leader not only for the quality of its products but also for its commitment to environmental and social sustainability, promoting the use of <span class="highlight">certified and eco-sustainable materials</span> and contributing to the well-being of the planet and the communities in which it operates.</p>
          </div>
        `,
    },
    {
      id: 4,
      title: '4. Purpose and scope *',
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm">The purpose of this report is to increase transparency regarding <strong>${name}</strong>'s greenhouse gas (GHG) emissions, improve environmental reporting and provide clear, accurate information to external stakeholders such as customers, suppliers, investors and regulators.</p>
            <p class="text-sm">The scope includes direct and indirect GHG emissions generated by <strong>${name}</strong>'s activities during ${reportingPeriodStart} – ${reportingPeriodEnd}, classified according to the categories defined by ISO 14064-1. This includes, but is not limited to, emissions from the use of fossil fuels, the purchase of electricity and heat, and the transport of goods and people.</p>
            <p class="text-sm">The document also covers <span class="highlight">[MitigationActivities]</span> undertaken to reduce environmental impact—such as implementing cleaner technologies, optimising business processes to reduce energy consumption, and launching projects that promote sustainability.</p>
            <p class="text-sm">Therefore, the object of certification for <strong>${name}</strong> consists of:</p>
            <ul class="list-disc pl-6 text-sm">
              <li>Evaluation and verification of the GHG inventory relating to the management of greenhouse gas emissions according to ISO 14064-1.</li>
              <li>Improved transparency and environmental responsibility in corporate reporting.</li>
            </ul>
          </div>
        `,
    },
    {
      id: 5,
      title: '5. Regulatory references *',
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm">In the context of managing greenhouse gas emissions and complying with international best practices, <strong>${name}</strong> adopts the following standards:</p>
            <ul class="list-disc pl-6 text-sm">
              <li>ISO 14064-1:2018 – "Greenhouse gases – Part 1: Specification with guidance at organization level for the quantification and reporting of greenhouse gas emissions and removals", which provides guidelines for organisations to quantify and report greenhouse gas emissions and removals.</li>
              <li>ISO/TR 14069 for additional guidance on applying consolidation approaches. This document offers practical guidance on managing corporate emissions according to international standards.</li>
              <li>The GHG Protocol, a widely recognised international standard for the quantification, management and reporting of greenhouse gas emissions. The GHG Protocol offers specific standards for companies and governments, facilitating a systematic and transparent approach to reducing GHG emissions.</li>
              <li><span class="highlight">[OtherRelevantStandards]</span>, such as national legislation or industry-specific guidelines applicable to <strong>${country}</strong>.</li>
            </ul>
          </div>
        `,
    },
    {
      id: 6,
      title: '6. Boundaries of the GHG inventory *',
      isEditing: false,
      content: ``,
    },
    {
      id: 7,
      title: '7. Quantification of GHG emissions',
      isEditing: false,
      content: ``,
    },
    {
      id: 8,
      title: '8. Comment on the results *',
      isEditing: false,
      content: '',
    },
    {
      id: 9,
      title: '9. GHG inventory quality management *',
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm"><strong>${name}</strong> has established processes to ensure that its greenhouse gas inventory is reliable and transparent. Data are collected from primary sources such as invoices, meter readings and travel logs, and are cross-checked by the sustainability team before being used in the calculator.</p>
            <p class="text-sm">To assess data reliability, the report employs an <strong>Uncertainty Matrix</strong> based on six indicators: data quality, relevance, temporal coverage, geographical coverage, technological coverage and completeness. Each activity is scored on a scale of 1 to 5 for each indicator; the average score is mapped to a percentage uncertainty. The combined uncertainty for an activity is then calculated using the quadrature method (square root of the sum of squared uncertainties), resulting in the uncertainty percentages presented in Chapter 7.</p>
            <p class="text-sm">Evidence supporting the data—such as invoices, meter readings or receipts—can be uploaded through the user interface and documented alongside notes explaining assumptions. Continuous improvement actions include <span class="highlight">[QualityImprovementActions]</span>, with the aim of enhancing data quality and reducing overall uncertainty in future inventories.</p>
          </div>
        `,
    },
    {
      id: 10,
      title: '10. Uncertainty assessment',
      isEditing: false,
      content: ``,
    },
    {
      id: 11,
      title: '11. Role of the organisation in the verification activity *',
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm">The organisation plays a pivotal role in the independent verification of this GHG inventory. It is responsible for preparing and organising all relevant documentation, granting verifiers access to data sources and engaging in interviews as needed. A base year (here <span class="highlight">${reportingYear}</span>) is established to serve as the reference for monitoring improvements over time.</p>
            <p class="text-sm">The organisation's responsibilities during verification include:</p>
            <ul class="list-disc pl-6 text-sm">
              <li><strong>Ensuring data availability and traceability</strong> – consolidating invoices, meter readings, supplier information and internal records so that verifiers can trace each emission back to a documented source.</li>
              <li><strong>Facilitating site visits and interviews</strong> – coordinating with facility managers and staff to provide verifiers with on-site access and explanations of data collection processes.</li>
              <li><strong>Maintaining alignment with sustainability goals</strong> – reviewing the GHG inventory to ensure it reflects current corporate objectives and identifying new opportunities for emission reduction.</li>
            </ul>
            <p class="text-sm">A periodic review process ensures accuracy, completeness and consistency. Regular reviews help detect errors or omissions, update emission factors and methodology to reflect best practices, and document the organisation's progress over time. This systematic documentation facilitates transparency for stakeholders and strengthens the credibility of reported data.</p>
          </div>
        `,
    },
    {
      id: 12,
      title: '12. Mitigation activities *',
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm">To reduce its carbon footprint, <strong>${name}</strong> has implemented a range of mitigation measures. These can be grouped into two categories: reduction activities and offsetting activities.</p>

            <h3 class="text-base font-bold">12.1. Reduction activities</h3>
            <p class="text-sm">The organisation is pursuing short-, medium- and long-term strategies to lower its direct and indirect emissions. Current initiatives include:</p>
            <ul class="list-disc pl-6 text-sm">
              <li><strong>Energy efficiency and renewable energy</strong> – investing in energy-efficient equipment, optimising production processes and purchasing electricity from renewable sources.</li>
              <li><strong>Employee engagement and awareness</strong> – raising staff awareness about energy use and encouraging sustainable practices (e.g. eco-driving, waste reduction).</li>
              <li><strong>Sustainable procurement and waste management</strong> – favouring suppliers with strong environmental performance and reducing waste through recycling and reuse programmes.</li>
            </ul>

            <h3 class="text-base font-bold">12.2. Carbon offsetting and compensation</h3>
            <p class="text-sm">In addition to reducing emissions, <strong>${name}</strong> engages in carbon-offsetting projects to neutralise unavoidable emissions. Examples include:</p>
            <ul class="list-disc pl-6 text-sm">
              <li><strong>Planting projects</strong> – supporting or implementing certified reforestation or afforestation programs, on third-party or company land, similar to the Forever Bamboo initiative, which sequester CO₂ over the long term.</li>
              <li><strong>Renewable energy certificates</strong> – purchasing certificates that finance renewable energy projects and guarantee the production of clean electricity.</li>
              <li><strong>Certified carbon credits</strong> – Offsetting, or using carbon credits (from removal or savings) certified by an authorized body, to offset residual emissions.</li>
            </ul>
            <!--p class="text-sm">The impact of these mitigation and offsetting activities is tracked over time to demonstrate progress toward carbon-neutrality goals. Detailed information about each project, including certificates and verification reports, is stored in the organisation's sustainability documentation.</p-->
          </div>
        `,
    },
    {
      id: 13,
      title: '13. Glossary',
      isEditing: false,
      content: `
          <div class="">
            <h3 class="text-base font-bold">Greenhouse gas terms</h3>
            <ul class="mb-2">
            <li><strong>Greenhouse gases (GHG):</strong> Gaseous constituent of the atmosphere, both natural and anthropogenic, that absorbs and emits radiation at specific wavelengths within the spectrum of infrared radiation emitted by the earth's surface, the atmosphere and clouds.</li>
            <li><strong>GHG source:</strong> Process that releases a GHG into the atmosphere.</li>
            <li><strong>GHG absorber:</strong> Process that removes a GHG from the atmosphere.</li>
            <li><strong>GHG reservoir:</strong> Component, other than the atmosphere, capable of accumulating, storing and releasing GHG.</li>
            <li><strong>GHG emission:</strong> Release of a GHG into the atmosphere.</li>
            <li><strong>GHG removal:</strong> Removal of a GHG from the atmosphere through the use of GHG absorbers.</li>
            <li><strong>GHG emission factor:</strong> Coefficient that relates GHG activity data to GHG emissions.</li>
            <li><strong>GHG removal factor:</strong> Coefficient that relates GHG activity data to GHG removal.</li>
            <li><strong>Direct GHG emission:</strong> GHG emission from GHG sources owned or controlled by the organisation.</li>
            <li><strong>Indirect GHG emission:</strong> GHG emission that occurs as a consequence of the operations and activities of an organization, but which is generated from GHG sources that are not owned or controlled.</li>
            <li><strong>Global warming potential (GWP):</strong> Index, based on the radiative properties of GHGs, that measures the radiant force after a pulse emission of a unit of mass of a given GHG compared to that of carbon dioxide (CO₂).</li>
            <li><strong>Carbon dioxide equivalent (CO₂e):</strong> Unit for comparing the radiant force of a GHG with that of carbon dioxide.</li>
            </ul>
            <h3 class="text-base font-bold">Terms related to the GHG inventory process</h3>
            <ul>
            <li><strong>GHG activity data:</strong> Quantitative measure of activity that results from a GHG emission or GHG removal.</li>
            <li><strong>Primary data:</strong> Quantified value of a process or activity resulting from a direct measurement or a calculation based on direct measurements.</li>
            <li><strong>Secondary data:</strong> Data obtained from sources other than primary data.</li>
            <li><strong>GHG inventory:</strong> List of GHG sources and GHG sinks and their respective quantified emissions and removals.</li>
            <li><strong>GHG report:</strong> Stand-alone document intended to communicate GHG-related information from an organization or GHG project to its intended users.</li>
            <li><strong>Monitoring:</strong> Continuous or periodic assessment of GHG emissions, removals or other GHG-related data.</li>
            <li><strong>Uncertainty:</strong> Parameter associated with the result of a quantification that characterizes dispersion of values reasonably attributable to the quantified value.</li>
            </ul>
            <h3 class="text-base font-bold">Organization, Stakeholder, and Audit Terms</h3>
            <ul>
            <li><strong>Installation:</strong> Single plant or series of plants or production processes (fixed or mobile) defined within a geographical boundary.</li>
            <li><strong>Organization:</strong> Person or group of people with responsibility, authority and interrelationships to achieve objectives.</li>
            <li><strong>Organizational boundary:</strong> Set of activities or facilities over which an organization exercises operational or financial control.</li>
            <li><strong>Reporting boundary:</strong> Set of GHG emissions or removals reported within organizational boundaries, including significant indirect emissions.</li>
            <li><strong>Verification:</strong> Process of evaluating historical data to determine whether assertions are materially correct.</li>
            <li><strong>Validation:</strong> Process of evaluating the reasonableness of assumptions, limitations and methods that support a statement about the outcome of future activities.</li>
            </ul>
          </div>
        `,
    },
  ];
}

function buildItalianSections(
  company: any,
  language: string = 'italian',
): ReportSection[] {
  const name = company?.companyName || "l'Azienda";
  const sector =
    company?.businessSector ||
    '<span class="highlight">[Settore aziendale]</span>';
  const country = getCountryName(
    company?.country,
    '<span class="highlight">[Paese]</span>',
    language,
  );
  const { city: parsedCity, formattedAddress: parsedAddr } =
    parseCompanyAddress(company?.address);
  const city = parsedCity || '<span class="highlight">[Città]</span>';
  const address =
    parsedAddr || '<span class="highlight">[Indirizzo sede]</span>';
  const foundationYear =
    company?.foundationYear || '<span class="highlight">[Anno]</span>';
  const productsOrServices =
    company?.productsOrServices ||
    '<span class="highlight">[Descrizione prodotti/servizi]</span>';
  const mission =
    company?.mission ||
    '<span class="highlight">essere un punto di riferimento nel suo settore, offrendo prodotti e servizi di alta qualità</span>';
  const vision =
    company?.vision ||
    "<span class=\"highlight\">essere un'azienda all'avanguardia attraverso l'innovazione continua e l'adozione delle tecnologie più recenti</span>";
  const reportingYear =
    company?.reportingYear ||
    '<span class="highlight">[Anno di riferimento]</span>';
  const reportingPeriodStart = '01/01/' + (reportingYear || '2023');
  const reportingPeriodEnd = '31/12/' + (reportingYear || '2023');
  const yearsOfExperience =
    company?.reportingYear &&
    company?.foundationYear &&
    !isNaN(parseInt(company.reportingYear)) &&
    !isNaN(parseInt(company.foundationYear))
      ? parseInt(company.reportingYear) - parseInt(company.foundationYear)
      : '<span class="highlight">[Anni di esperienza]</span>';

  // Calculate emission data placeholders
  const totalCO2e = '<span class="highlight">[TotalCO2e]</span>';
  const totalCO2 = '<span class="highlight">[TotalCO2]</span>';
  const totalCH4 = '<span class="highlight">[TotalCH4]</span>';
  const totalN2O = '<span class="highlight">[TotalN2O]</span>';
  const overallUncertainty =
    '<span class="highlight">[OverallUncertainty]</span>';

  return [
    {
      id: 1,
      title: '1. Approccio metodologico',
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm">Questo approccio si basa sui principi chiave di rilevanza, completezza, coerenza, trasparenza e accuratezza, garantendo che il bilancio delle emissioni rifletta le emissioni effettive di gas serra e le attività dell'organizzazione per monitorarle e ridurle.</p>
            <p class="text-sm">In primo luogo, sono stati identificati i confini organizzativi di <strong>${name}</strong>, determinando le attività, i siti e le unità operative da includere nell'inventario delle emissioni. Le fonti di emissione sono state quindi mappate e suddivise in emissioni dirette (Categoria 1) ed emissioni indirette (Categorie 2-6). Le emissioni dirette riguardano le attività interne sotto il controllo dell'azienda, mentre le emissioni indirette includono il consumo di elettricità, il trasporto delle merci, gli spostamenti dei dipendenti e la produzione o lo smaltimento dei prodotti utilizzati dall'organizzazione.</p>
            <p class="text-sm">La raccolta dei dati ha coinvolto la consultazione di documenti interni, fatture energetiche, schede tecniche e altri registri pertinenti. Sono stati applicati fattori di emissione specifici per ciascun combustibile o attività e, ove possibile, sono stati utilizzati fattori specifici per paese o regione per migliorare l'accuratezza. Tutti i dati e le metodologie sono stati documentati in dettaglio per garantire trasparenza e coerenza. Le incertezze di misurazione sono state valutate e registrate e sono state identificate azioni di riduzione laddove necessario.</p>
            <p class="text-sm">Questo rapporto include anche una sezione sulle strategie di riduzione delle emissioni implementate da <strong>${name}</strong>, evidenziando gli sforzi dell'organizzazione per ridurre il proprio impatto ambientale.</p>
          </div>
        `,
    },
    {
      id: 2,
      title: '2. Introduzione *',
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm">Nello scenario globale attuale, caratterizzato da un aumento senza precedenti delle temperature medie della Terra, l'attenzione sui cambiamenti climatici e l'impatto ambientale delle attività umane è in costante crescita. I cambiamenti climatici, causati principalmente dall'emissione di gas serra (GHG) nell'atmosfera, rappresentano una delle sfide ambientali, sociali ed economiche più significative del nostro tempo. Questi gas—tra cui anidride carbonica (CO₂), metano (CH₄) e protossido di azoto (N₂O)—contribuiscono all'effetto serra, un processo naturale che, se amplificato dalle attività umane, può portare al riscaldamento globale con conseguenze potenzialmente disastrose.</p>
            <p class="text-sm">Il primo passo per affrontare questa sfida è misurare e rendicontare le emissioni di GHG, ovvero determinare l'impronta di carbonio di un'organizzazione. L'impronta di carbonio rappresenta la quantità totale di gas serra emessi direttamente o indirettamente attraverso le attività di un'organizzazione, espressa in termini di equivalenti di CO₂. Quantificare queste emissioni è fondamentale per gestirle e ridurle, poiché solo ciò che può essere misurato può essere migliorato.</p>
            <p class="text-sm"><strong>${name}</strong> riconosce il proprio ruolo in questo sforzo globale e si impegna a rendicontare in modo trasparente le proprie emissioni di gas serra. Pubblicando questo rapporto, l'organizzazione mira a identificare le sue principali fonti di emissioni, valutare le opportunità di riduzione e coinvolgere gli stakeholder nel suo percorso di sostenibilità.</p>
          </div>
        `,
    },
    {
      id: 3,
      title: '3. Profilo aziendale *',
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm"><strong>${name}</strong>, fondata nel <strong>${foundationYear}</strong> a <strong>${city}</strong>, <strong>${country}</strong>, opera nel settore <strong>${sector}</strong>. Specializzata in <strong>${productsOrServices}</strong>, l'azienda si distingue per il suo core business focalizzato sulla creatività e sull'offerta di prodotti di alta qualità.</p>
            <p class="text-sm">Allo stesso tempo, <strong>${name}</strong> si impegna a promuovere la produzione di beni che soddisfino elevati standard di qualità e siano conformi alle normative vigenti in materia di protezione ed etica del lavoro. Un elemento distintivo del suo approccio è l'utilizzo di <span class="highlight">materiali certificati o provenienti da fonti responsabili</span>, spesso inclusi materiali ecologici come <span class="highlight">materiali riciclati</span>. L'azienda ritiene essenziale affrontare le questioni sociali e ambientali per onorare gli impegni presi con i clienti; questo approccio non solo mantiene un'alta qualità del prodotto, ma riflette anche un impegno più ampio verso la sostenibilità.</p>

            <h3 class="text-base font-bold">3.1. Missione</h3>
            <p class="text-sm">La missione di <strong>${name}</strong> è essere un punto di riferimento nel suo settore, offrendo <span class="highlight">prodotti</span> e servizi di alta qualità per <span class="highlight">i suoi mercati target</span>. Con oltre <strong>${yearsOfExperience}</strong> di esperienza nel settore <strong>${sector}</strong>, l'azienda si impegna a fornire soluzioni creative e innovative ai propri clienti—dal design iniziale alla produzione finale. La sua missione è soddisfare le esigenze dei clienti offrendo prodotti che rispettano i più alti standard di qualità, sostenibilità e conformità alle normative vigenti.</p>

            <h3 class="text-base font-bold">3.2. Visione</h3>
            <p class="text-sm">La visione di <strong>${name}</strong> è essere un'azienda all'avanguardia nel settore <strong>${sector}</strong> attraverso l'innovazione continua e l'adozione delle tecnologie più recenti. L'organizzazione cerca di espandere la propria presenza nel mercato internazionale, consolidando la propria reputazione come partner affidabile e di fiducia. Mira a diventare leader non solo per la qualità dei suoi prodotti, ma anche per il suo impegno verso la sostenibilità ambientale e sociale, promuovendo l'uso di <span class="highlight">materiali certificati ed eco-sostenibili</span> e contribuendo al benessere del pianeta e delle comunità in cui opera.</p>
          </div>
        `,
    },
    {
      id: 4,
      title: '4. Scopo e ambito *',
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm">Lo scopo di questo rapporto è aumentare la trasparenza riguardo alle emissioni di gas serra (GHG) di <strong>${name}</strong>, migliorare la rendicontazione ambientale e fornire informazioni chiare e accurate agli stakeholder esterni come clienti, fornitori, investitori e autorità di regolamentazione.</p>
            <p class="text-sm">L'ambito include le emissioni di GHG dirette e indirette generate dalle attività di <strong>${name}</strong> durante il periodo <strong>${reportingPeriodStart}</strong> – <strong>${reportingPeriodEnd}</strong>, classificate secondo le categorie definite dalla norma ISO 14064-1. Ciò include, ma non si limita a, emissioni derivanti dall'uso di combustibili fossili, dall'acquisto di elettricità e calore e dal trasporto di merci e persone.</p>
            <p class="text-sm">Il documento copre anche le <span class="highlight">[Attività di mitigazione]</span> intraprese per ridurre l'impatto ambientale—come l'implementazione di tecnologie più pulite, l'ottimizzazione dei processi aziendali per ridurre il consumo energetico e il lancio di progetti che promuovono la sostenibilità.</p>
            <p class="text-sm">Pertanto, l'oggetto della certificazione per <strong>${name}</strong> consiste in:</p>
            <ul class="list-disc pl-6 text-sm">
              <li>Valutazione e verifica dell'inventario GHG relativo alla gestione delle emissioni di gas serra secondo la norma ISO 14064-1.</li>
              <li>Miglioramento della trasparenza e della responsabilità ambientale nella rendicontazione aziendale.</li>
            </ul>
          </div>
        `,
    },
    {
      id: 5,
      title: '5. Riferimenti normativi *',
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm">Nel contesto della gestione delle emissioni di gas serra e della conformità alle migliori pratiche internazionali, <strong>${name}</strong> adotta i seguenti standard:</p>
            <ul class="list-disc pl-6 text-sm">
              <li>ISO 14064-1:2018 – "Gas serra – Parte 1: Specifiche con guida a livello organizzativo per la quantificazione e la rendicontazione delle emissioni e degli assorbimenti di gas serra", che fornisce linee guida alle organizzazioni per quantificare e rendicontare le emissioni e gli assorbimenti di gas serra.</li>
              <li>ISO/TR 14069 per ulteriori indicazioni sull'applicazione degli approcci di consolidamento. Questo documento offre indicazioni pratiche sulla gestione delle emissioni aziendali secondo gli standard internazionali.</li>
              <li>Il GHG Protocol, uno standard internazionale ampiamente riconosciuto per la quantificazione, gestione e rendicontazione delle emissioni di gas serra. Il GHG Protocol offre standard specifici per aziende e governi, facilitando un approccio sistematico e trasparente alla riduzione delle emissioni di GHG.</li>
              <li><span class="highlight">[Altri standard rilevanti]</span>, come la legislazione nazionale o le linee guida specifiche del settore applicabili a <strong>${country}</strong>.</li>
            </ul>
          </div>
        `,
    },
    {
      id: 6,
      title: "6. Confini dell'inventario GHG *",
      isEditing: false,
      content: ``,
    },
    {
      id: 7,
      title: '7. Quantificazione delle emissioni GHG',
      isEditing: false,
      content: ``,
    },
    {
      id: 8,
      title: '8. Commento sui risultati *',
      isEditing: false,
      content: ``,
    },
    {
      id: 9,
      title: "9. Gestione della qualità dell'inventario GHG *",
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm"><strong>${name}</strong> ha stabilito processi per garantire che il suo inventario dei gas serra sia affidabile e trasparente. I dati sono raccolti da fonti primarie come fatture, letture dei contatori e registri di viaggio e vengono verificati incrociati dal team di sostenibilità prima di essere utilizzati nel calcolatore.</p>
            <p class="text-sm">Per valutare l'affidabilità dei dati, il rapporto utilizza una Matrice di Incertezza basata su sei indicatori: qualità dei dati, rilevanza, copertura temporale, copertura geografica, copertura tecnologica e completezza. Ogni attività viene valutata su una scala da 1 a 5 per ciascun indicatore; il punteggio medio viene mappato a una percentuale di incertezza. L'incertezza combinata per un'attività viene quindi calcolata utilizzando il metodo della quadratura (radice quadrata della somma dei quadrati delle incertezze), con conseguente determinazione delle percentuali di incertezza presentate nel Capitolo 7.</p>
            <p class="text-sm">Le prove a supporto dei dati—come fatture, letture dei contatori o ricevute—possono essere caricate tramite l'interfaccia utente e documentate insieme a note che spiegano le ipotesi. Le azioni di miglioramento continuo includono <span class="highlight">[Azioni di miglioramento della qualità]</span>, con l'obiettivo di migliorare la qualità dei dati e ridurre l'incertezza complessiva negli inventari futuri.</p>
          </div>
        `,
    },
    {
      id: 10,
      title: "10. Valutazione dell'incertezza",
      isEditing: false,
      content: ``,
    },
    {
      id: 11,
      title: "11. Ruolo dell'organizzazione nell'attività di verifica *",
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm">L'organizzazione svolge un ruolo fondamentale nella verifica indipendente di questo inventario GHG. È responsabile della preparazione e organizzazione di tutta la documentazione pertinente, della concessione dell'accesso ai verificatori alle fonti dei dati e della partecipazione a interviste secondo necessità. Un anno base (qui <span class="highlight">${reportingYear}</span>) è stabilito per servire come riferimento per monitorare i miglioramenti nel tempo.</p>
            <p class="text-sm">Le responsabilità dell'organizzazione durante la verifica includono:</p>
            <ul class="list-disc pl-6 text-sm">
              <li><strong>Garantire la disponibilità e la tracciabilità dei dati</strong> – consolidare fatture, letture dei contatori, informazioni sui fornitori e registri interni in modo che i verificatori possano tracciare ogni emissione fino a una fonte documentata.</li>
              <li><strong>Facilitare visite in sito e interviste</strong> – coordinarsi con i responsabili delle strutture e il personale per fornire ai verificatori accesso in sito e spiegazioni dei processi di raccolta dati.</li>
              <li><strong>Mantenere l'allineamento con gli obiettivi di sostenibilità</strong> – rivedere l'inventario GHG per garantire che rifletta gli attuali obiettivi aziendali e identificare nuove opportunità per la riduzione delle emissioni.</li>
            </ul>
            <p class="text-sm">Un processo di revisione periodica garantisce accuratezza, completezza e coerenza. Revisioni regolari aiutano a rilevare errori o omissioni, aggiornare i fattori di emissione e la metodologia per riflettere le migliori pratiche e documentare i progressi dell'organizzazione nel tempo. Questa documentazione sistematica facilita la trasparenza per gli stakeholder e rafforza la credibilità dei dati riportati.</p>
          </div>
        `,
    },
    {
      id: 12,
      title: '12. Attività di mitigazione *',
      isEditing: false,
      content: `
          <div class="">
            <p class="text-sm">Per ridurre la propria impronta di carbonio, <strong>${name}</strong> ha implementato una serie di misure di mitigazione. Queste possono essere raggruppate in due categorie: attività di riduzione e attività di compensazione.</p>

            <h3 class="text-base font-bold">12.1. Attività di riduzione</h3>
            <p class="text-sm">L'organizzazione persegue strategie a breve, medio e lungo termine per ridurre le proprie emissioni dirette e indirette. Le iniziative attuali includono:</p>
            <ul class="list-disc pl-6 text-sm">
              <li><strong>Efficienza energetica ed energia rinnovabile</strong> – investire in apparecchiature ad alta efficienza energetica, ottimizzare i processi produttivi e acquistare elettricità da fonti rinnovabili.</li>
              <li><strong>Coinvolgimento e sensibilizzazione dei dipendenti</strong> – aumentare la consapevolezza del personale sull'uso dell'energia e incoraggiare pratiche sostenibili (es. guida ecologica, riduzione dei rifiuti).</li>
              <li><strong>Approvvigionamento sostenibile e gestione dei rifiuti</strong> – favorire fornitori con solide prestazioni ambientali e ridurre i rifiuti attraverso programmi di riciclo e riutilizzo.</li>
            </ul>

            <h3 class="text-base font-bold">12.2. Compensazione del carbonio</h3>
            <p class="text-sm">Oltre a ridurre le emissioni, <strong>${name}</strong> partecipa a progetti di compensazione del carbonio per neutralizzare le emissioni inevitabili. Esempi includono:</p>
            <ul class="list-disc pl-6 text-sm">
              <li><strong>Progetti di piantumazione</strong> – sostenere o implementare programmi certificati di riforestazione o afforestazione, su terreni terzi o su terreni aziendali, simili all'iniziativa Forever Bambù, che sequestrano CO₂ nel lungo termine.</li>
              <li><strong>Certificati di energia rinnovabile</strong> – acquistare certificati che finanziano progetti di energiarinnovabile e garantiscono la produzione di elettricità pulita.</li>
              <li><strong>Crediti di carbonio certificati</strong> – Offsetting ovvero utilizzare crediti di carbonio (da rimozione o da risparmio) certificati da ente abilitato, per compensare le emissioni residue.</li>
            </ul>
            <!--p class="text-sm">L'impatto di queste attività di mitigazione e compensazione è monitorato nel tempo per dimostrare i progressi verso gli obiettivi di neutralità carbonica. Informazioni dettagliate su ciascun progetto, inclusi certificati e rapporti di verifica, sono conservate nella documentazione di sostenibilità dell'organizzazione.</p-->
          </div>
        `,
    },
    {
      id: 13,
      title: '13. Glossario',
      isEditing: false,
      content: `
          <div class="">
            <h3 class="text-base font-bold">Termini relativi ai gas serra</h3>
            <ul>
            <li><strong>Gas serra (GHG):</strong> Costituente gassoso dell'atmosfera, sia naturale che antropico, che assorbe ed emette radiazioni a specifiche lunghezze d'onda all'interno dello spettro delle radiazioni infrarosse emesse dalla superficie terrestre, dall'atmosfera e dalle nuvole.</li>
            <li><strong>Sorgente di GHG:</strong> Processo che rilascia un GHG nell'atmosfera.</li>
            <li><strong>Assorbitore di GHG:</strong> Processo che rimuove un GHG dall'atmosfera.</li>
            <li><strong>Serbatoio di GHG:</strong> Componente, diverso dall'atmosfera, in grado di accumulare, conservare e rilasciare GHG.</li>
            <li><strong>Emissione di GHG:</strong> Rilascio di un GHG nell'atmosfera.</li>
            <li><strong>Rimozione di GHG:</strong> Prelievo di un GHG dall'atmosfera attraverso l'uso di assorbitori di GHG.</li>
            <li><strong>Fattore di emissione di GHG:</strong> Coefficiente che mette in relazione i dati di attività con l'emissione di GHG.</li>
            <li><strong>Fattore di rimozione di GHG:</strong> Coefficiente che mette in relazione i dati di attività con la rimozione di GHG.</li>
            <li><strong>Emissione diretta di GHG:</strong> Emissione da sorgenti di GHG di proprietà o controllate dall'organizzazione.</li>
            <li><strong>Emissione indiretta di GHG:</strong> Emissione che deriva dalle attività dell'organizzazione ma generata da sorgenti non controllate.</li>
            <li><strong>Potenziale di riscaldamento globale (GWP):</strong> Indice, basato sulle proprietà radiative dei GHG, che misura la forza radiante di un GHG rispetto al CO₂.</li>
            <li><strong>Biossido di carbonio equivalente (CO₂e):</strong> Unità per confrontare la forza radiante di un GHG con quella del biossido di carbonio.</li>
            </ul>
            <h3 class="text-base font-bold">Termini relativi al processo di inventario dei GHG</h3>
            <ul>
            <li><strong>Dati di attività GHG:</strong> Misura quantitativa di attività che risulta da un'emissione o rimozione di GHG.</li>
            <li><strong>Dati primari:</strong> Valore quantificato di un processo o attività risultante da una misurazione diretta o da un calcolo basato su misurazioni dirette.</li>
            <li><strong>Dati secondari:</strong> Dati ottenuti da fonti diverse dai dati primari.</li>
            <li><strong>Inventario GHG:</strong> Elenco di sorgenti e pozzi di GHG e delle rispettive emissioni e rimozioni quantificate.</li>
            <li><strong>Rapporto GHG:</strong> Documento autonomo destinato a comunicare informazioni relative ai GHG da un'organizzazione o progetto GHG ai suoi utenti previsti.</li>
            <li><strong>Monitoraggio:</strong> Valutazione continua o periodica delle emissioni di GHG, rimozioni o altri dati relativi ai GHG.</li>
            <li><strong>Incertezza:</strong> Parametro associato al risultato di una quantificazione che caratterizza la dispersione di valori ragionevolmente attribuibili al valore quantificato.</li>
            </ul>
            <h3 class="text-base font-bold">Termini relativi a organizzazioni, parti interessate e verifiche</h3>
            <ul>
            <li><strong>Impianto:</strong> Singolo stabilimento o serie di stabilimenti o processi produttivi (fissi o mobili) definiti entro un confine geografico.</li>
            <li><strong>Organizzazione:</strong> Persona o gruppo di persone con responsabilità, autorità e interrelazioni per raggiungere obiettivi.</li>
            <li><strong>Confine organizzativo:</strong> Insieme di attività o strutture su cui un'organizzazione esercita controllo operativo o finanziario.</li>
            <li><strong>Confine di rendicontazione:</strong> Insieme di emissioni o rimozioni di GHG riportate entro i confini organizzativi, comprese le emissioni indirette significative.</li>
            <li><strong>Verifica:</strong> Processo di valutazione dei dati storici per determinare se le affermazioni sono materialmente corrette.</li>
            <li><strong>Validazione:</strong> Processo di valutazione della ragionevolezza di assunzioni, limitazioni e metodi che supportano un'affermazione sul risultato di attività future.</li>
          </div>
        `,
    },
  ];
}
interface SignificanceRow {
  categoryId: string;
  questionId: string;
  elemento: string;
  criteria: {
    magnitude: string;
    influence: string;
    riskOpportunity: string;
    stakeholderEngagement: string;
    dataAccessibility: string;
  };
  ls: number;
  include: boolean;
  comment: string;
}

interface CategoryLS {
  id: string;
  significanceMatrix: SignificanceRow[];
}

export interface CompanyProfile {
  name: string;
  foundationYear?: number;
  city?: string;
  country?: string;
  sector?: string; // already collected
  productsServices?: string; // free text
  mission?: string;
  vision?: string;
  sites?: string[]; // optional list of sites
  reportingYear?: number;
}
interface CategoryTotals {
  co2: number;
  ch4: number;
  n2o: number;
  co2e: number;
}
